import { useState, useEffect, useCallback, useRef } from "react";
import { http, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Save, Loader2, Eye, Trash2, Plus, Edit3, ChevronDown, ChevronRight, ChevronUp, X, Image as ImageIcon, Package, Truck, CheckCircle, Clock, Palette, MessageSquare, BookOpen, Users, Hash, MapPin, ArrowRight, Search, RefreshCw, Printer, ShoppingBag, Award, Link2 } from "lucide-react";
import ImageField from "@/components/ImageField";
import OrderShippingActions from "@/components/OrderShippingActions";
import AdminMerchMore from "@/components/AdminMerchMore";
import DrawingTracker from "@/components/DrawingTracker";

const FIELD_GROUPS = [
  {
    key: "hero", label: "Hero Section",
    fields: [
      { key: "hero_heading", label: "Heading", type: "text" },
      { key: "hero_tagline", label: "Tagline", type: "text" },
      { key: "hero_text", label: "Description", type: "textarea" },
      { key: "hero_image", label: "Hero Image", type: "image" },
    ],
  },
  {
    key: "hand_drawn", label: "Drawn by Hand Section",
    fields: [
      { key: "hand_drawn_heading", label: "Heading", type: "text" },
      { key: "hand_drawn_text", label: "Text", type: "textarea" },
      { key: "hand_drawn_media", label: "Image/Media", type: "image" },
    ],
  },
  {
    key: "originals", label: "One Drawing. One Original.",
    fields: [
      { key: "originals_heading", label: "Heading", type: "text" },
      { key: "originals_text", label: "Text", type: "textarea" },
    ],
  },
  {
    key: "artist", label: "Meet the Artist",
    fields: [
      { key: "artist_heading", label: "Heading", type: "text" },
      { key: "artist_text", label: "Bio (line breaks = paragraphs)", type: "textarea" },
      { key: "artist_photo", label: "Photo", type: "image" },
    ],
  },
  {
    key: "commission", label: "Custom Commissions",
    fields: [
      { key: "commission_enabled", label: "Show Commission Section", type: "toggle" },
      { key: "commission_heading", label: "Heading", type: "text" },
      { key: "commission_tagline", label: "Tagline", type: "text" },
      { key: "commission_text", label: "Description", type: "textarea" },
      { key: "commission_starting_price", label: "Starting Price ($)", type: "number" },
      { key: "commission_cta_label", label: "Button Text", type: "text" },
      { key: "commission_badge", label: "Badge Text (e.g. COMMISSIONS OPEN)", type: "text" },
      { key: "commission_features", label: "Features (pipe-separated: Feature 1|Feature 2|...)", type: "text" },
      { key: "commission_image", label: "Commission Image", type: "image" },
    ],
  },
  {
    key: "discount", label: "Multi-Print Discount",
    fields: [
      { key: "multi_print_discount_enabled", label: "Enable Multi-Print Discount", type: "toggle" },
      { key: "multi_print_discount_per_item", label: "Discount Per Print ($)", type: "number" },
      { key: "multi_print_discount_min_qty", label: "Minimum Qty for Discount", type: "number" },
    ],
  },
  {
    key: "email", label: "Email Signup",
    fields: [
      { key: "email_heading", label: "Heading", type: "text" },
      { key: "email_text", label: "Description", type: "textarea" },
    ],
  },
  {
    key: "shipping", label: "Shipping & Pickup",
    fields: [
      { key: "shipping_flat_rate", label: "Flat Rate Shipping ($)", type: "number" },
      { key: "shipping_free_over", label: "Free Shipping Over ($)", type: "number" },
      { key: "pickup_enabled", label: "Enable Pickup / Meet-up Option", type: "toggle" },
      { key: "pickup_info", label: "Pickup Info (shown at checkout)", type: "textarea" },
    ],
  },
  {
    key: "pages_gallery", label: "Page: Gallery",
    fields: [
      { key: "page_gallery_enabled", label: "Enable Gallery Page", type: "toggle" },
      { key: "gallery_heading", label: "Heading", type: "text" },
      { key: "gallery_subheading", label: "Subheading", type: "textarea" },
    ],
  },
  {
    key: "pages_originals", label: "Page: Originals",
    fields: [
      { key: "page_originals_enabled", label: "Enable Originals Page", type: "toggle" },
      { key: "originals_page_heading", label: "Heading", type: "text" },
      { key: "originals_page_text", label: "Hero Text", type: "textarea" },
      { key: "originals_page_subtext", label: "Story Text", type: "textarea" },
      { key: "originals_page_image", label: "Feature Image", type: "image" },
    ],
  },
  {
    key: "pages_commissions", label: "Page: Commissions",
    fields: [
      { key: "page_commissions_enabled", label: "Enable Commissions Page", type: "toggle" },
      { key: "commissions_page_heading", label: "Heading", type: "text" },
      { key: "commissions_page_text", label: "Description", type: "textarea" },
      { key: "commissions_page_process", label: "Process Steps (pipe-separated: Step 1|Step 2|...)", type: "text" },
      { key: "commissions_page_faq", label: "FAQ (pipe-separated: Question|Answer|Question|Answer|...)", type: "textarea" },
    ],
  },
  {
    key: "pages_about", label: "Page: About the Artist",
    fields: [
      { key: "page_about_enabled", label: "Enable About Page", type: "toggle" },
      { key: "about_page_heading", label: "Heading", type: "text" },
      { key: "about_page_text", label: "Bio (line breaks = paragraphs)", type: "textarea" },
      { key: "about_page_image", label: "Photo (falls back to Artist Photo above)", type: "image" },
      { key: "about_page_quote", label: "Quote", type: "text" },
      { key: "about_page_values", label: "Values (pipe-separated: Title|Desc|Title|Desc|...)", type: "textarea" },
    ],
  },
  {
    key: "pages_process", label: "Page: The Process",
    fields: [
      { key: "page_process_enabled", label: "Enable Process Page", type: "toggle" },
      { key: "process_page_heading", label: "Heading", type: "text" },
      { key: "process_page_text", label: "Description", type: "textarea" },
      { key: "process_page_steps", label: "Steps (pipe-separated: Title|Desc|Title|Desc|...)", type: "textarea" },
      { key: "process_page_tools", label: "Tools (pipe-separated: Name|Desc|Name|Desc|...)", type: "textarea" },
    ],
  },
];

const EMPTY_ART = {
  title: "", slug: "", description: "", main_image: "",
  art_type: "Print", tags: [], price: null, quantity: null, show_quantity: true, signed: false,
  original_status: "Available", dimensions: "", medium: "Marker on paper",
  year: "", status: "active", external_url: "", sort_order: 0,
  process_images: [],
  prints_enabled: false, print_price: null, print_quantity: null, print_signed: false,
  on_sale: false, sale_price: null,
};

/* ─── Shared: Generate Price Tag HTML for an artwork ─── */
function generateTagHtml(art) {
  const vendorCode = art.vendor_code || "MT26-0000";
  const artImg = art.main_image ? resolveImage(art.main_image) : null;
  const price = Number(art.price || 0);
  const hasSale = art.on_sale && art.sale_price != null && Number(art.sale_price) > 0 && Number(art.sale_price) < price;
  const displayPrice = hasSale ? Number(art.sale_price) : price;
  const dollars = displayPrice.toFixed(0);
  const cents = (displayPrice % 1 !== 0) ? (displayPrice % 1).toFixed(2).substring(1) : ".00";
  const artType = art.art_type || "PRINT";
  const isOriginal = artType === "Original";
  const title = (art.title || "UNTITLED").toUpperCase();
  const truncTitle = title.length > 28 ? title.substring(0, 26) + "..." : title;
  const slug = art.slug || art.id;
  const siteBase = window.location.origin;
  const artworkUrl = `${siteBase}/marker-collection/${slug}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(artworkUrl)}&margin=4`;
  const showPrintsAvailable = isOriginal && art.prints_enabled && art.print_price;
  const isCharity = art.charity?.enabled && art.charity?.nonprofit_name;
  const accentColor = hasSale ? "#FF3B30" : isCharity ? "#4ade80" : "#C9A055";

  const saleBadgeHtml = hasSale ? `<div style="position:absolute;top:0;right:0;background:#FF3B30;color:#fff;font-family:'Saira Condensed',sans-serif;font-size:10px;font-weight:800;letter-spacing:0.12em;padding:3px 8px;text-transform:uppercase;">SALE</div>` : "";

  const priceHtml = hasSale
    ? `<div style="display:flex;align-items:baseline;gap:6px;">
         <span style="font-family:'Saira Condensed',sans-serif;font-size:58px;font-weight:900;line-height:1;color:#FF3B30;">$${dollars}</span>
         <span style="font-family:'Saira Condensed',sans-serif;font-size:22px;font-weight:700;color:#FF3B30;line-height:1;position:relative;top:-2px;">${cents}</span>
         <span style="font-family:'Saira Condensed',sans-serif;font-size:22px;font-weight:700;color:#999;text-decoration:line-through;line-height:1;position:relative;top:-2px;margin-left:4px;">$${price.toFixed(0)}</span>
       </div>`
    : `<div style="display:flex;align-items:baseline;gap:3px;">
         <span style="font-family:'Saira Condensed',sans-serif;font-size:${showPrintsAvailable ? "50" : "58"}px;font-weight:900;line-height:1;color:#111;">$${dollars}</span>
         <span style="font-family:'Saira Condensed',sans-serif;font-size:22px;font-weight:700;color:#999;line-height:1;position:relative;top:-2px;">${cents}</span>
       </div>`;

  const printsAvailableHtml = showPrintsAvailable
    ? `<div style="display:flex;align-items:center;gap:6px;margin-top:4px;">
         <div style="background:#4F46E5;color:#fff;font-family:'Saira Condensed',sans-serif;font-size:9px;font-weight:800;letter-spacing:0.12em;padding:3px 8px;text-transform:uppercase;">PRINTS FROM $${Number(art.print_price).toFixed(0)}</div>
       </div>`
    : "";

  const charityBarHtml = isCharity
    ? `<div style="position:absolute;bottom:44px;left:0;right:0;height:26px;background:linear-gradient(90deg,#065f46,#047857,#065f46);display:flex;align-items:center;justify-content:center;gap:8px;">
         <svg width="12" height="12" viewBox="0 0 24 24" fill="#4ade80"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
         <span style="font-family:'Saira Condensed',sans-serif;font-size:10px;font-weight:800;letter-spacing:0.15em;color:#fff;text-transform:uppercase;">FOR A CAUSE &mdash; ${(art.charity.nonprofit_name || "").toUpperCase()}</span>
       </div>`
    : "";

  return `<div style="width:576px;height:384px;background:#FFFFFF;font-family:'DM Sans',sans-serif;position:relative;overflow:hidden;">
    <!-- Accent top -->
    <div style="position:absolute;top:0;left:0;right:0;height:4px;background:linear-gradient(90deg,${accentColor},${accentColor}88,${accentColor});"></div>

    <!-- Main content: two-column layout -->
    <div style="display:flex;height:${isCharity ? "314" : "340"}px;padding-top:4px;">

      <!-- LEFT: Artwork Image -->
      <div style="width:210px;flex-shrink:0;display:flex;align-items:center;justify-content:center;padding:18px 8px 18px 20px;">
        <div style="width:178px;height:100%;position:relative;background:#f7f5f0;border:1px solid #e8e4dc;box-shadow:0 2px 12px rgba(0,0,0,0.08),0 0 0 1px rgba(201,160,85,0.15);">
          ${artImg
            ? `<img src="${artImg}" alt="${art.title || ''}" style="width:100%;height:100%;object-fit:cover;display:block;" crossorigin="anonymous" />`
            : `<div style="width:100%;height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
                <span style="font-size:8px;color:#bbb;letter-spacing:0.1em;text-transform:uppercase;">No Image</span>
              </div>`}
          ${isOriginal ? `<div style="position:absolute;top:0;left:0;background:#FF3B30;color:#fff;font-family:'Saira Condensed',sans-serif;font-size:9px;font-weight:800;letter-spacing:0.15em;padding:3px 8px;text-transform:uppercase;">Original</div>` : ""}
          ${saleBadgeHtml}
        </div>
      </div>

      <!-- RIGHT: Info Column -->
      <div style="flex:1;padding:16px 20px 16px 12px;display:flex;flex-direction:column;position:relative;">
        <!-- Vendor code — top right -->
        <div style="position:absolute;top:12px;right:16px;text-align:right;">
          <div style="font-family:'Saira Condensed',sans-serif;font-size:30px;font-weight:900;line-height:1;color:#1a1a1a;letter-spacing:0.03em;">${vendorCode}</div>
        </div>

        <!-- Branding -->
        <div>
          <div style="font-family:'Permanent Marker',cursive;font-size:13px;color:#C9A055;line-height:1;">THE MARKER COLLECTION</div>
          <div style="font-size:8px;color:#aaa;letter-spacing:0.15em;text-transform:uppercase;margin-top:2px;">MarkerCollection.com</div>
        </div>

        <!-- Separator -->
        <div style="height:1px;background:linear-gradient(90deg,${accentColor} 0%,#e5e5e5 40%,transparent 100%);margin:10px 0 12px 0;"></div>

        <!-- Title -->
        <div style="font-family:'Saira Condensed',sans-serif;font-size:${showPrintsAvailable ? "28" : "32"}px;font-weight:800;line-height:1.05;color:#111;max-width:300px;">${truncTitle}</div>

        <!-- Badges -->
        <div style="display:flex;align-items:center;gap:7px;margin-top:8px;flex-wrap:wrap;">
          <span style="font-size:9px;font-weight:700;letter-spacing:0.2em;text-transform:uppercase;background:${isOriginal ? "#FF3B30" : "#C9A055"};color:#fff;padding:2px 8px;">${artType.toUpperCase()}</span>
          ${art.dimensions ? `<span style="font-size:9px;color:#888;letter-spacing:0.08em;">${art.dimensions}</span>` : ""}
          ${art.signed ? `<span style="font-size:9px;color:#4ade80;font-weight:600;letter-spacing:0.08em;">&#10003; Signed</span>` : ""}
        </div>

        <!-- Spacer -->
        <div style="flex:1;"></div>

        <!-- Prints Available Banner -->
        ${printsAvailableHtml}

        <!-- Price + QR row -->
        <div style="display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:4px;">
          ${priceHtml}
          ${`<div style="width:72px;height:72px;border:1px solid #e5e5e5;display:flex;align-items:center;justify-content:center;background:#fff;flex-shrink:0;">
                <img src="${qrSrc}" alt="QR" style="width:64px;height:64px;object-fit:contain;" />
              </div>`}
        </div>
      </div>
    </div>

    <!-- Charity Bar (above bottom bar) -->
    ${charityBarHtml}

    <!-- Bottom bar -->
    <div style="position:absolute;bottom:0;left:0;right:0;height:44px;background:#111;display:flex;align-items:center;justify-content:space-between;padding:0 24px;">
      <div style="display:flex;align-items:center;gap:10px;">
        <div style="font-family:'Permanent Marker',cursive;font-size:12px;color:#C9A055;line-height:1;">THE MARKER COLLECTION</div>
        <div style="width:1px;height:14px;background:#333;"></div>
        <div style="font-size:7px;color:#777;letter-spacing:0.12em;">Original Artwork Drawn Entirely With a Sharpie</div>
      </div>
      <div style="font-size:8px;color:#555;letter-spacing:0.1em;">Originals &bull; Prints &bull; Commissions</div>
    </div>
  </div>`;
}

export default function AdminMarkerCollection() {
  const [settings, setSettings] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderStats, setOrderStats] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [commissionStats, setCommissionStats] = useState(null);
  const [queueItems, setQueueItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openGroup, setOpenGroup] = useState("hero");
  const [tab, setTab] = useState("artworks");
  const [editArt, setEditArt] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);
  const [viewCommission, setViewCommission] = useState(null);
  const [printTagArt, setPrintTagArt] = useState(null);

  const handleBatchPrintTags = (arts) => {
    if (!arts.length) { toast.error("No active artworks to print"); return; }
    const tagsHtml = arts.map(a => generateTagHtml(a)).join('<div style="page-break-after:always;height:0;"></div>');
    const win = window.open("", "_blank", "width=700,height=500");
    win.document.write(`<!DOCTYPE html><html><head>
      <title>All Price Tags — Marker Collection</title>
      <link rel="preconnect" href="https://fonts.googleapis.com">
      <link href="https://fonts.googleapis.com/css2?family=Saira+Condensed:wght@700;800;900&family=DM+Sans:wght@400;500;700&family=Permanent+Marker&display=swap" rel="stylesheet">
      <style>
        @page { size: 6in 4in landscape; margin: 0; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #fff; }
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style>
    </head><body>${tagsHtml}</body></html>`);
    win.document.close();
    win.onload = () => { win.focus(); win.print(); };
  };

  const [charityApps, setCharityApps] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [registryStats, setRegistryStats] = useState(null);
  const [marketOrders, setMarketOrders] = useState([]);
  const [nameWinners, setNameWinners] = useState([]);

  const load = useCallback(async () => {
    try {
      const [sRes, aRes, oRes, stRes, cRes, csRes, qRes, caRes, sgRes, rgRes, moRes, nwRes] = await Promise.all([
        http.get("/marker-collection/admin/settings"),
        http.get("/marker-collection/admin/artworks"),
        http.get("/marker-collection/admin/orders"),
        http.get("/marker-collection/admin/orders/stats"),
        http.get("/marker-collection/admin/commissions"),
        http.get("/marker-collection/admin/commissions/stats"),
        http.get("/marker-collection/admin/queue"),
        http.get("/marker-collection/admin/charity/applications").catch(() => ({ data: [] })),
        http.get("/marker-collection/public/suggestions").catch(() => ({ data: [] })),
        http.get("/marker-collection/admin/registry/stats").catch(() => ({ data: null })),
        http.get("/marker-collection/admin/market-orders").catch(() => ({ data: [] })),
        http.get("/marker-collection/admin/name-winners").catch(() => ({ data: [] })),
      ]);
      setSettings(sRes.data);
      setArtworks(aRes.data);
      setOrders(oRes.data);
      setOrderStats(stRes.data);
      setCommissions(cRes.data);
      setCommissionStats(csRes.data);
      setQueueItems(qRes.data);
      setCharityApps(caRes.data);
      setSuggestions(sgRes.data || []);
      setRegistryStats(rgRes.data);
      setMarketOrders(moRes.data || []);
      setNameWinners(nwRes.data || []);
    } catch {
      toast.error("Failed to load Marker Collection data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await http.put("/marker-collection/admin/settings", settings);
      setSettings(data);
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const deleteArtwork = async (id) => {
    if (!window.confirm("Delete this artwork permanently?")) return;
    try {
      await http.delete(`/marker-collection/admin/artworks/${id}`);
      toast.success("Artwork deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="mc-admin-loading">
        <Loader2 className="animate-spin text-[#C9A055]" size={24} />
      </div>
    );
  }

  return (
    <div data-testid="mc-admin">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-ui text-[11px] tracking-[0.32em] uppercase text-[#C9A055]">
          Marker Collection
        </h2>
        <a href="/marker-collection" target="_blank" rel="noopener noreferrer"
          className="btn-outline text-xs flex items-center gap-1.5" data-testid="mc-admin-preview">
          <Eye size={12} /> Preview Page
        </a>
      </div>

      {/* Sub-tabs */}
      <div className="mb-6 border-b border-white/10 pb-3 overflow-x-auto -mx-4 px-4 scrollbar-hide" data-testid="mc-admin-tabs">
        <div className="flex gap-2 min-w-max">
        {[
          ["settings", "Page Settings"],
          ["artworks", "Artworks"],
          ["orders", `Orders${orderStats?.pending ? ` (${orderStats.pending})` : ""}`],
          ["commissions", `Commissions${commissionStats?.new ? ` (${commissionStats.new})` : ""}`],
          ["queue", `Queue (${queueItems.filter(q => q.status !== "complete").length})`],
          ["charity-apps", `Charity Apps${charityApps.filter(a => a.status === "new").length ? ` (${charityApps.filter(a => a.status === "new").length})` : ""}`],
          ["suggestions", `Challenges${suggestions.filter(s => s.status === "pending").length ? ` (${suggestions.filter(s => s.status === "pending").length})` : ""}`],
          ["registry", `Registry${registryStats?.claimed ? ` (${registryStats.claimed})` : ""}`],
          ["merch", "Merch & More"],
          ["market-orders", `Market Orders${marketOrders.filter(o => o.status === "pending").length ? ` (${marketOrders.filter(o => o.status === "pending").length})` : ""}`],
          ["name-winners", `Name Winners${nameWinners.filter(w => w.status === "new").length ? ` (${nameWinners.filter(w => w.status === "new").length})` : ""}`],
        ].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)}
            className={`font-ui text-[10px] tracking-[0.22em] uppercase px-4 py-2 border whitespace-nowrap transition-colors ${
              tab === v ? "bg-[#C9A055] text-black border-[#C9A055]"
                : "bg-transparent text-[#F5F2EB] border-white/10 hover:border-[#C9A055]"
            }`} data-testid={`mc-tab-${v}`}>
            {l}
          </button>
        ))}
        </div>
      </div>

      {/* ─── Settings Tab ─── */}
      {tab === "settings" && settings && (
        <div data-testid="mc-settings-panel">
          {FIELD_GROUPS.map(group => {
            const isOpen = openGroup === group.key;
            return (
              <div key={group.key} className="border border-white/10 mb-2">
                <button onClick={() => setOpenGroup(isOpen ? null : group.key)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
                  data-testid={`mc-group-${group.key}`}>
                  <span className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#F5F2EB]">{group.label}</span>
                  {isOpen ? <ChevronDown size={14} className="text-[#C9A055]" /> : <ChevronRight size={14} className="text-gray-500" />}
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 space-y-4">
                    {group.fields.map(f => (
                      <div key={f.key}>
                        {f.type === "image" ? (
                          <ImageField label={f.label} value={settings[f.key] || ""} onChange={val => updateField(f.key, val)} testid={`mc-${f.key}`} />
                        ) : f.type === "toggle" ? (
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input type="checkbox" checked={settings[f.key] !== false} onChange={e => updateField(f.key, e.target.checked)} className="accent-[#C9A055] w-4 h-4" data-testid={`mc-toggle-${f.key}`} />
                            <span className="font-ui text-[10px] tracking-[0.2em] text-[#F5F2EB]">{f.label}</span>
                          </label>
                        ) : f.type === "number" ? (
                          <>
                            <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">{f.label}</label>
                            <input type="number" step="any" className="stc-input w-full font-mono text-[12px]" value={settings[f.key] ?? ""} onChange={e => updateField(f.key, e.target.value === "" ? null : Number(e.target.value))} data-testid={`mc-input-${f.key}`} />
                          </>
                        ) : f.type === "textarea" ? (
                          <>
                            <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">{f.label}</label>
                            <textarea className="stc-input w-full font-mono text-[12px] min-h-[80px]" value={settings[f.key] || ""} onChange={e => updateField(f.key, e.target.value)} data-testid={`mc-input-${f.key}`} />
                          </>
                        ) : (
                          <>
                            <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">{f.label}</label>
                            <input className="stc-input w-full font-mono text-[12px]" value={settings[f.key] || ""} onChange={e => updateField(f.key, e.target.value)} data-testid={`mc-input-${f.key}`} />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <button onClick={saveSettings} disabled={saving}
            className="mt-4 bg-[#C9A055] text-black px-6 py-2.5 font-ui text-[11px] tracking-[0.22em] uppercase flex items-center gap-2 hover:bg-[#b8915a] transition-colors disabled:opacity-50"
            data-testid="mc-save-settings">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Settings
          </button>
        </div>
      )}

      {/* ─── Artworks Tab ─── */}
      {tab === "artworks" && (
        <div data-testid="mc-artworks-panel">
          <div className="flex items-center justify-between mb-2">
            <span className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] uppercase">
              {artworks.length} Artwork{artworks.length !== 1 ? "s" : ""}
            </span>
            <button onClick={() => setEditArt({ ...EMPTY_ART })}
              className="bg-[#C9A055] text-black px-4 py-2 font-ui text-[10px] tracking-[0.22em] uppercase flex items-center gap-1.5 hover:bg-[#b8915a] transition-colors"
              data-testid="mc-add-artwork-btn">
              <Plus size={14} /> Add Artwork
            </button>
          </div>
          <div className="flex gap-2 mb-4 flex-nowrap overflow-x-auto scrollbar-hide pb-1">
            <button onClick={async () => {
              try {
                const res = await http.post("/marker-collection/admin/artworks/assign-all-vendor-codes");
                const assigned = res.data?.assigned || [];
                if (assigned.length === 0) toast.info("All artworks already have vendor codes");
                else { toast.success(`Assigned ${assigned.length} vendor code(s)`); load(); }
              } catch { toast.error("Failed to assign codes"); }
            }}
              className="btn-outline text-[10px] px-3 py-2 flex items-center gap-1.5 border-[#C9A055]/40 text-[#C9A055] hover:bg-[#C9A055]/10 whitespace-nowrap flex-shrink-0"
              data-testid="mc-assign-all-codes">
              <Hash size={12} /> Assign Codes
            </button>
            <button onClick={() => handleBatchPrintTags(artworks.filter(a => a.status === "active" && !a.linked_parent_id))}
              className="btn-outline text-[10px] px-3 py-2 flex items-center gap-1.5 border-[#C9A055]/40 text-[#C9A055] hover:bg-[#C9A055]/10 whitespace-nowrap flex-shrink-0"
              data-testid="mc-batch-print-tags">
              <Printer size={12} /> Print All Tags
            </button>
            <button onClick={() => window.open("/marker-collection/booth", "_blank")}
              className="btn-outline text-[10px] px-3 py-2 flex items-center gap-1.5 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 whitespace-nowrap flex-shrink-0"
              data-testid="mc-booth-mode-btn">
              <Package size={12} /> Booth Mode
            </button>
          </div>

          {artworks.length === 0 ? (
            <div className="border-2 border-dashed border-white/10 text-center py-12">
              <p className="text-[#8a857d] text-sm">No artworks yet. Click "Add Artwork" to create your first piece.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {artworks.map(art => (
                <ArtworkRow key={art.id} art={art} onEdit={() => setEditArt({ ...art })} onDelete={() => deleteArtwork(art.id)} onPrintTag={async () => {
                  if (!art.vendor_code) {
                    try {
                      const res = await http.post(`/marker-collection/admin/artworks/${art.id}/assign-vendor-code`);
                      art.vendor_code = res.data?.vendor_code;
                    } catch {}
                  }
                  setPrintTagArt({ ...art });
                }} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── Orders Tab ─── */}
      {tab === "orders" && (
        <div data-testid="mc-orders-panel">
          {/* Stats */}
          {orderStats && (
            <div className="grid grid-cols-4 gap-3 mb-6">
              {[
                ["Total", orderStats.total, "text-[#F5F2EB]"],
                ["Paid", orderStats.paid, "text-emerald-400"],
                ["Pending", orderStats.pending, "text-amber-400"],
                ["Revenue", `$${(orderStats.revenue || 0).toFixed(2)}`, "text-[#C9A055]"],
              ].map(([label, val, color]) => (
                <div key={label} className="border border-white/10 bg-[#0a0a0a] p-4 text-center">
                  <div className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] uppercase mb-1">{label}</div>
                  <div className={`font-display text-xl ${color}`}>{val}</div>
                </div>
              ))}
            </div>
          )}

          {/* Orders list */}
          {orders.length === 0 ? (
            <div className="border-2 border-dashed border-white/10 text-center py-12">
              <Package size={24} className="mx-auto mb-3 text-[#8a857d]" />
              <p className="text-[#8a857d] text-sm">No orders yet.</p>
            </div>
          ) : (
            <div className="border border-white/10 bg-[#0a0a0a]">
              <div className="grid grid-cols-6 px-4 py-3 border-b border-white/10 font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">
                <div>Date</div><div className="col-span-2">Customer</div><div>Artwork</div><div>Total</div><div>Status</div>
              </div>
              {orders.map(o => (
                <div key={o.id} onClick={() => setViewOrder(o)}
                  className="grid grid-cols-6 px-4 py-3 border-b border-white/5 items-center text-sm cursor-pointer hover:bg-white/5"
                  data-testid={`mc-order-row-${o.id}`}>
                  <div className="font-mono text-[#B5B0A7] text-xs">{new Date(o.created_at).toLocaleDateString()}</div>
                  <div className="col-span-2 min-w-0">
                    <div className="text-[#F4F1EA] text-xs truncate">{o.customer_name}</div>
                    <div className="text-[#8a857d] text-[10px] truncate">{o.customer_email}</div>
                  </div>
                  <div className="text-[#B5B0A7] text-xs truncate">{o.artwork_title}</div>
                  <div className="text-[#C9A055] text-xs">${(o.price || 0).toFixed(2)}</div>
                  <div>
                    <OrderStatusBadge status={o.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── Commissions Tab ─── */}
      {tab === "commissions" && (
        <AdminCommissionsPanel
          commissions={commissions}
          commissionStats={commissionStats}
          onViewCommission={setViewCommission}
          onRefresh={load}
        />
      )}

      {/* ─── Queue Tab ─── */}
      {tab === "queue" && (
        <DrawingTracker items={queueItems} onRefresh={load} />
      )}

      {/* ─── Charity Applications Tab ─── */}
      {tab === "charity-apps" && (
        <CharityAppsPanel apps={charityApps} onRefresh={load} />
      )}

      {tab === "suggestions" && (
        <SuggestionsPanel suggestions={suggestions} onRefresh={load} />
      )}

      {tab === "registry" && (
        <RegistryPanel artworks={artworks} onRefresh={load} />
      )}

      {tab === "merch" && (
        <AdminMerchMore />
      )}

      {/* ─── Market Orders Tab ─── */}
      {tab === "market-orders" && (
        <MarketOrdersPanel orders={marketOrders} onRefresh={load} />
      )}

      {/* ─── Name Winners Tab ─── */}
      {tab === "name-winners" && (
        <NameWinnersPanel winners={nameWinners} onRefresh={load} />
      )}

      {/* ─── Commission Detail Modal ─── */}
      {viewCommission && (
        <CommissionDetailModal
          commission={viewCommission}
          onClose={() => setViewCommission(null)}
          onSaved={() => { setViewCommission(null); load(); }}
        />
      )}

      {/* ─── Order Detail Modal ─── */}
      {viewOrder && (
        <OrderDetailModal
          order={viewOrder}
          onClose={() => setViewOrder(null)}
          onSaved={() => { setViewOrder(null); load(); }}
        />
      )}

      {/* ─── Artwork Editor Modal ─── */}
      {editArt && (
        <ArtworkEditor
          artwork={editArt}
          onClose={() => setEditArt(null)}
          onSaved={() => { setEditArt(null); load(); }}
        />
      )}

      {/* ─── Print Price Tag Modal ─── */}
      {printTagArt && (
        <PriceTagModal
          art={printTagArt}
          onClose={() => setPrintTagArt(null)}
        />
      )}
    </div>
  );
}

/* ─── Artwork Row ─── */
function ArtworkRow({ art, onEdit, onDelete, onPrintTag }) {
  const img = art.main_image ? resolveImage(art.main_image) : null;
  const isSold = art.original_status === "Sold";
  const isDraft = art.status !== "active";
  const hasSale = art.on_sale && art.sale_price > 0 && art.sale_price < art.price;
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-3" data-testid={`mc-art-row-${art.id}`}>
      {/* Row 1: Image + Full-width Title/Code/Price */}
      <div className="flex gap-3 items-center">
        <div className="w-12 h-12 bg-black/50 flex-shrink-0 overflow-hidden">
          {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ImageIcon size={14} className="text-gray-600" /></div>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-[#F5F2EB] truncate">{art.title}</div>
          <div className="flex items-center gap-2 mt-0.5 text-[9px]">
            {art.vendor_code && <span className="font-mono font-bold text-[#C9A055]">{art.vendor_code}</span>}
            {art.price != null && (
              hasSale
                ? <span className="font-mono"><span className="text-red-400 font-bold">${Number(art.sale_price).toFixed(0)}</span> <span className="line-through text-gray-500">${Number(art.price).toFixed(0)}</span></span>
                : <span className="font-mono text-gray-400">${Number(art.price).toFixed(2)}</span>
            )}
            {art.art_type && <span className="text-[#C9A055]/70">{art.art_type}</span>}
          </div>
        </div>
      </div>
      {/* Row 2: Badges + Actions */}
      <div className="flex items-center mt-2 ml-[60px]">
        <div className="flex flex-wrap gap-1 flex-1 min-w-0">
          {art.signed && <span className="text-[7px] tracking-wider uppercase bg-emerald-900/30 text-emerald-400 px-1.5 py-0.5">Signed</span>}
          {isSold && <span className="text-[7px] tracking-wider uppercase bg-red-900/30 text-red-400 px-1.5 py-0.5">Sold</span>}
          {isDraft && <span className="text-[7px] tracking-wider uppercase bg-yellow-900/30 text-yellow-400 px-1.5 py-0.5">Draft</span>}
          {hasSale && <span className="text-[7px] tracking-wider uppercase bg-red-900/20 text-red-400 px-1.5 py-0.5">Sale</span>}
          {art.quantity != null ? (
            <span className={`text-[7px] tracking-wider uppercase px-1.5 py-0.5 ${art.quantity <= 0 ? "bg-red-900/30 text-red-400" : art.quantity <= 5 ? "bg-amber-900/20 text-amber-400" : "bg-white/5 text-gray-500"}`}>
              {art.quantity <= 0 ? "Out" : `Qty: ${art.quantity}`}
            </span>
          ) : (
            <span className="text-[7px] tracking-wider uppercase bg-white/5 text-gray-600 px-1.5 py-0.5">∞</span>
          )}
          {art.linked_print_id && <span className="text-[7px] tracking-wider uppercase bg-blue-900/20 text-blue-400 px-1.5 py-0.5">Prints</span>}
        </div>
        <div className="flex gap-1 flex-shrink-0 ml-2">
          <button onClick={onPrintTag} className="p-1.5 text-[#C9A055] hover:bg-[#C9A055]/10 transition-colors" title="Price Tag" data-testid={`mc-tag-${art.id}`}>
            <Package size={14} />
          </button>
          <button onClick={onEdit} className="p-1.5 text-[#8a857d] hover:text-white transition-colors" title="Edit" data-testid={`mc-edit-${art.id}`}>
            <Edit3 size={14} />
          </button>
          <button onClick={onDelete} className="p-1.5 text-gray-600 hover:text-red-400 transition-colors" title="Delete" data-testid={`mc-delete-${art.id}`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Artwork Editor (modal overlay) ─── */
function ArtworkEditor({ artwork, onClose, onSaved }) {
  const isNew = !artwork.id;
  const [form, setForm] = useState({ ...artwork, tags: (artwork.tags || []).join(", ") });
  const [saving, setSaving] = useState(false);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const autoSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  };

  const handleTitleChange = (val) => {
    set("title", val);
    if (isNew || !artwork.slug) {
      set("slug", autoSlug(val));
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.slug.trim()) { toast.error("Slug is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
        price: form.price ? Number(form.price) : null,
        sale_price: form.sale_price != null && form.sale_price !== "" ? Number(form.sale_price) : null,
        on_sale: !!form.on_sale,
        quantity: form.quantity != null && form.quantity !== "" ? Number(form.quantity) : null,
        show_quantity: form.show_quantity !== false,
        sort_order: Number(form.sort_order) || 0,
        process_images: (form.process_images || []).filter(s => s.image),
      };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.linked_parent_id;
      delete payload.linked_print_id;

      if (isNew) {
        await http.post("/marker-collection/admin/artworks", payload);
        toast.success("Artwork created");
      } else {
        await http.patch(`/marker-collection/admin/artworks/${artwork.id}`, payload);
        toast.success("Artwork updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 bg-black/80 overflow-y-auto" data-testid="mc-art-editor">
      <div className="bg-[#111] border border-white/10 w-full max-w-2xl mx-4 mb-10 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="font-ui text-[11px] tracking-[0.22em] uppercase text-[#C9A055]">
            {isNew ? "Add Artwork" : "Edit Artwork"}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors" data-testid="mc-editor-close">
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Title *</label>
              <input className="stc-input w-full text-[12px]" value={form.title} onChange={e => handleTitleChange(e.target.value)} data-testid="mc-editor-title" />
            </div>
            <div>
              <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Slug *</label>
              <input className="stc-input w-full text-[12px] font-mono" value={form.slug} onChange={e => set("slug", e.target.value)} data-testid="mc-editor-slug" />
            </div>
          </div>

          <div>
            <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Description</label>
            <textarea className="stc-input w-full text-[12px] min-h-[60px]" value={form.description} onChange={e => set("description", e.target.value)} data-testid="mc-editor-desc" />
          </div>

          <ImageField label="Main Image" value={form.main_image} onChange={val => set("main_image", val)} testid="mc-editor-image" />

          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Type</label>
              <select className="stc-input w-full text-[12px]" value={form.art_type} onChange={e => set("art_type", e.target.value)} data-testid="mc-editor-type">
                <option>Original</option>
                <option>Print</option>
                <option>Limited Edition</option>
                <option>Digital</option>
              </select>
            </div>
            <div>
              <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Price ($)</label>
              <input className="stc-input w-full text-[12px]" type="number" step="0.01" min="0" value={form.price ?? ""} onChange={e => set("price", e.target.value || null)} placeholder="0.00" data-testid="mc-editor-price" />
              <label className="flex items-center gap-2 cursor-pointer mt-2">
                <input type="checkbox" checked={!!form.on_sale} onChange={e => set("on_sale", e.target.checked)} className="accent-red-500 w-4 h-4" data-testid="mc-editor-on-sale" />
                <span className="font-ui text-[8px] text-red-400 tracking-[0.15em]">ON SALE</span>
              </label>
              {form.on_sale && (
                <div className="mt-1.5">
                  <label className="font-ui text-[8px] tracking-[0.2em] text-red-400 block mb-0.5">SALE PRICE ($)</label>
                  <input className="stc-input w-full text-[12px] font-mono border-red-500/30" type="number" step="0.01" min="0" value={form.sale_price ?? ""} onChange={e => set("sale_price", e.target.value === "" ? null : e.target.value)} placeholder="Sale price" data-testid="mc-editor-sale-price" />
                </div>
              )}
            </div>
            <div>
              <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Quantity</label>
              <input className="stc-input w-full text-[12px]" type="number" min="0" value={form.quantity ?? ""} onChange={e => set("quantity", e.target.value === "" ? null : e.target.value)} placeholder="Unlimited" data-testid="mc-editor-quantity" />
              <label className="flex items-center gap-2 cursor-pointer mt-1.5">
                <input type="checkbox" checked={form.show_quantity !== false} onChange={e => set("show_quantity", e.target.checked)} className="accent-[#C9A055]" data-testid="mc-editor-show-qty" />
                <span className="font-ui text-[8px] text-[#6a6660]">Show stock on page</span>
              </label>
            </div>
            <div>
              <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Status</label>
              <select className="stc-input w-full text-[12px]" value={form.status} onChange={e => set("status", e.target.value)} data-testid="mc-editor-status">
                <option value="active">Active (Visible)</option>
                <option value="draft">Draft (Hidden)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Original Status</label>
              <select className="stc-input w-full text-[12px]" value={form.original_status} onChange={e => set("original_status", e.target.value)} data-testid="mc-editor-orig-status">
                <option>Available</option>
                <option>Sold</option>
              </select>
            </div>
            <div>
              <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Dimensions</label>
              <input className="stc-input w-full text-[12px]" value={form.dimensions} onChange={e => set("dimensions", e.target.value)} placeholder='e.g. 11" x 14"' data-testid="mc-editor-dims" />
            </div>
            <div>
              <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Year</label>
              <input className="stc-input w-full text-[12px]" value={form.year} onChange={e => set("year", e.target.value)} placeholder="2025" data-testid="mc-editor-year" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Medium</label>
              <input className="stc-input w-full text-[12px]" value={form.medium} onChange={e => set("medium", e.target.value)} data-testid="mc-editor-medium" />
            </div>
            <div>
              <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Sort Order</label>
              <input className="stc-input w-full text-[12px]" type="number" value={form.sort_order} onChange={e => set("sort_order", e.target.value)} data-testid="mc-editor-sort" />
            </div>
          </div>

          <div>
            <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Tags (comma-separated)</label>
            <input className="stc-input w-full text-[12px]" value={form.tags} onChange={e => set("tags", e.target.value)} placeholder="Abstract, Portrait, Nature" data-testid="mc-editor-tags" />
          </div>

          <div>
            <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">External Link (optional — overrides detail page)</label>
            <input className="stc-input w-full text-[12px] font-mono" value={form.external_url} onChange={e => set("external_url", e.target.value)} placeholder="https://..." data-testid="mc-editor-url" />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.signed} onChange={e => set("signed", e.target.checked)} className="accent-[#C9A055]" data-testid="mc-editor-signed" />
            <span className="font-ui text-[10px] tracking-wider text-[#8a857d]">Signed by Artist</span>
          </label>

          {/* ═══ Booth / Price Tag ═══ */}
          <div className="border-t border-white/10 pt-4 mt-2">
            <span className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055] uppercase">Booth / Price Tag</span>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Vendor Code</label>
                <input className="stc-input w-full text-[12px] font-mono" value={form.vendor_code || ""} onChange={e => set("vendor_code", e.target.value)} placeholder="MT26-0001 (auto-assigned)" data-testid="mc-editor-vendor-code" />
              </div>
              <div>
                <ImageField label="QR Code Image" value={form.qr_code_image} onChange={val => set("qr_code_image", val)} testid="mc-editor-qr" thumbnail />
              </div>
            </div>
          </div>

          {/* ═══ Also Offer Prints ═══ */}
          {!form.linked_parent_id && (
            <div className="border-t border-white/10 pt-4 mt-2" data-testid="mc-prints-section">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055] uppercase">Also Offer Prints</span>
                  <span className="font-ui text-[8px] text-[#6a6660] ml-2">Auto-create a linked print listing</span>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!form.prints_enabled} onChange={e => set("prints_enabled", e.target.checked)} className="accent-[#C9A055]" data-testid="mc-prints-toggle" />
                  <span className="font-ui text-[9px] tracking-wider text-[#8a857d]">Enable</span>
                </label>
              </div>
              {form.prints_enabled && (
                <div className="space-y-3 bg-[#0a0908] border border-[#C9A055]/20 p-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="font-ui text-[9px] tracking-[0.32em] text-[#C9A055] block mb-1">Print Price ($) *</label>
                      <input className="stc-input w-full text-[12px]" type="number" step="0.01" min="0" value={form.print_price ?? ""} onChange={e => set("print_price", e.target.value ? Number(e.target.value) : null)} placeholder="25.00" data-testid="mc-print-price" />
                    </div>
                    <div>
                      <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Print Quantity</label>
                      <input className="stc-input w-full text-[12px]" type="number" min="0" value={form.print_quantity ?? ""} onChange={e => set("print_quantity", e.target.value === "" ? null : Number(e.target.value))} placeholder="Unlimited" data-testid="mc-print-qty" />
                    </div>
                    <div className="flex items-end pb-1">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={!!form.print_signed} onChange={e => set("print_signed", e.target.checked)} className="accent-[#C9A055]" data-testid="mc-print-signed" />
                        <span className="font-ui text-[9px] tracking-wider text-[#8a857d]">Prints are signed</span>
                      </label>
                    </div>
                  </div>
                  <p className="font-ui text-[8px] text-[#6a6660]">
                    A linked print listing will be auto-created with the same title, images, and description. Edits to shared fields sync automatically.
                  </p>
                  {form.linked_print_id && (
                    <div className="text-[9px] text-emerald-400 font-ui tracking-wider flex items-center gap-1">
                      <CheckCircle size={10} /> Linked print exists and will be kept in sync
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Notice for auto-synced prints */}
          {form.linked_parent_id && (
            <div className="border border-[#C9A055]/30 bg-[#C9A055]/5 p-3 text-[#C9A055] text-xs font-ui tracking-wider" data-testid="mc-linked-print-notice">
              This is an auto-synced print. Shared fields (title, images, description) sync from the original. Edit the original to update them.
            </div>
          )}

          {/* Mark by Mark — Process Images */}
          <div className="border-t border-white/10 pt-4 mt-2">
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055] uppercase">Mark by Mark</span>
                <span className="font-ui text-[8px] text-[#6a6660] ml-2">The creation process (up to 10 images)</span>
              </div>
              {(form.process_images || []).length < 10 && (
                <button type="button" onClick={() => {
                  const imgs = [...(form.process_images || []), { image: "", caption: "" }];
                  set("process_images", imgs);
                }} className="font-ui text-[9px] tracking-wider text-[#C9A055] border border-[#C9A055]/30 px-3 py-1 hover:bg-[#C9A055]/10 transition-colors" data-testid="mc-process-add">
                  + Add Step
                </button>
              )}
            </div>
            {(form.process_images || []).length === 0 && (
              <p className="text-[#6a6660] text-xs italic">No process images yet. Add steps to show how this piece was created.</p>
            )}
            <div className="space-y-3">
              {(form.process_images || []).map((step, i) => (
                <div key={i} className="border border-white/5 bg-[#080808] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-ui text-[9px] tracking-wider text-[#8a857d]">Step {i + 1} of {(form.process_images || []).length}</span>
                    <div className="flex gap-1">
                      {i > 0 && (
                        <button type="button" onClick={() => {
                          const imgs = [...form.process_images];
                          [imgs[i - 1], imgs[i]] = [imgs[i], imgs[i - 1]];
                          set("process_images", imgs);
                        }} className="text-[#8a857d] hover:text-white text-[10px] px-1">&#9650;</button>
                      )}
                      {i < (form.process_images || []).length - 1 && (
                        <button type="button" onClick={() => {
                          const imgs = [...form.process_images];
                          [imgs[i], imgs[i + 1]] = [imgs[i + 1], imgs[i]];
                          set("process_images", imgs);
                        }} className="text-[#8a857d] hover:text-white text-[10px] px-1">&#9660;</button>
                      )}
                      <button type="button" onClick={() => {
                        const imgs = form.process_images.filter((_, j) => j !== i);
                        set("process_images", imgs);
                      }} className="text-red-500 hover:text-red-300 text-[10px] px-1" data-testid={`mc-process-remove-${i}`}>&times;</button>
                    </div>
                  </div>
                  <ImageField
                    label={`Image`}
                    value={step.image || ""}
                    onChange={(val) => {
                      const imgs = [...form.process_images];
                      imgs[i] = { ...imgs[i], image: val };
                      set("process_images", imgs);
                    }}
                    testid={`mc-process-img-${i}`}
                    thumbnail
                  />
                  <input
                    className="stc-input w-full text-[11px] mt-2"
                    placeholder="Caption (optional) — e.g. 'Starting the outline'"
                    value={step.caption || ""}
                    onChange={(e) => {
                      const imgs = [...form.process_images];
                      imgs[i] = { ...imgs[i], caption: e.target.value };
                      set("process_images", imgs);
                    }}
                    data-testid={`mc-process-caption-${i}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* ═══ Charity / Nonprofit Fundraiser ═══ */}
          <div className="border-t border-white/10 pt-4 mt-2" data-testid="mc-charity-section">
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055] uppercase">Charity Fundraiser</span>
                <span className="font-ui text-[8px] text-[#6a6660] ml-2">Link this artwork to a nonprofit cause</span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!(form.charity && form.charity.enabled)} onChange={e => {
                  const c = { ...(form.charity || {}), enabled: e.target.checked };
                  set("charity", c);
                }} className="accent-[#C9A055]" data-testid="mc-charity-toggle" />
                <span className="font-ui text-[9px] tracking-wider text-[#8a857d]">Enable</span>
              </label>
            </div>
            {form.charity?.enabled && (() => {
              const ch = form.charity || {};
              const setCh = (k, v) => set("charity", { ...ch, [k]: v });
              return (
                <div className="space-y-3 bg-[#0a0908] border border-[#C9A055]/20 p-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Nonprofit Name *</label>
                      <input className="stc-input w-full text-[12px]" value={ch.nonprofit_name || ""} onChange={e => setCh("nonprofit_name", e.target.value)} placeholder="Hope House" data-testid="mc-charity-name" />
                    </div>
                    <div>
                      <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Website</label>
                      <input className="stc-input w-full text-[12px] font-mono" value={ch.nonprofit_url || ""} onChange={e => setCh("nonprofit_url", e.target.value)} placeholder="https://hopehouse.org" data-testid="mc-charity-url" />
                    </div>
                  </div>
                  <ImageField label="Nonprofit Logo" value={ch.nonprofit_logo || ""} onChange={v => setCh("nonprofit_logo", v)} testid="mc-charity-logo" thumbnail />
                  <div>
                    <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">About this nonprofit</label>
                    <textarea className="stc-input w-full text-[12px] min-h-[50px]" value={ch.nonprofit_description || ""} onChange={e => setCh("nonprofit_description", e.target.value)} placeholder="Brief description of the organization's mission..." data-testid="mc-charity-desc" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="font-ui text-[9px] tracking-[0.32em] text-[#C9A055] block mb-1">Original Sold For ($)</label>
                      <input className="stc-input w-full text-[12px]" type="number" step="0.01" min="0" value={ch.original_sold_amount || ""} onChange={e => setCh("original_sold_amount", e.target.value ? parseFloat(e.target.value) : 0)} placeholder="0.00" data-testid="mc-charity-orig-amount" />
                      <p className="font-ui text-[8px] text-[#6a6660] mt-1">What the original raised at auction/raffle</p>
                    </div>
                    <div>
                      <label className="font-ui text-[9px] tracking-[0.32em] text-[#C9A055] block mb-1">Donation Per Print ($)</label>
                      <input className="stc-input w-full text-[12px]" type="number" step="0.01" min="0" value={ch.donation_per_print || ""} onChange={e => setCh("donation_per_print", e.target.value ? parseFloat(e.target.value) : 0)} placeholder="5.00" data-testid="mc-charity-per-print" />
                      <p className="font-ui text-[8px] text-[#6a6660] mt-1">Fixed $ donated per print sold</p>
                    </div>
                    <div>
                      <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Goal Amount ($)</label>
                      <input className="stc-input w-full text-[12px]" type="number" step="0.01" min="0" value={ch.goal_amount || ""} onChange={e => setCh("goal_amount", e.target.value ? parseFloat(e.target.value) : 0)} placeholder="Optional" data-testid="mc-charity-goal" />
                      <p className="font-ui text-[8px] text-[#6a6660] mt-1">Optional fundraising target</p>
                    </div>
                  </div>
                  <div>
                    <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">Manual Print Revenue ($)</label>
                    <input className="stc-input w-full text-[12px]" type="number" step="0.01" min="0" value={ch.manual_print_revenue || ""} onChange={e => setCh("manual_print_revenue", e.target.value ? parseFloat(e.target.value) : 0)} placeholder="0.00" data-testid="mc-charity-manual-rev" />
                    <p className="font-ui text-[8px] text-[#6a6660] mt-1">Add revenue from offline/manual print sales. This gets added to auto-tracked web orders.</p>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-white/10">
          <button onClick={onClose} className="btn-outline text-xs px-4 py-2" data-testid="mc-editor-cancel">Cancel</button>
          <button onClick={save} disabled={saving}
            className="bg-[#C9A055] text-black px-6 py-2 font-ui text-[10px] tracking-[0.22em] uppercase flex items-center gap-2 hover:bg-[#b8915a] transition-colors disabled:opacity-50"
            data-testid="mc-editor-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {isNew ? "Create Artwork" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ─── Order Status Badge ─── */
function OrderStatusBadge({ status }) {
  const styles = {
    pending: "text-amber-400 bg-amber-900/20",
    paid: "text-emerald-400 bg-emerald-900/20",
    shipped: "text-blue-400 bg-blue-900/20",
    fulfilled: "text-emerald-300 bg-emerald-900/30",
    cancelled: "text-red-400 bg-red-900/20",
  };
  const icons = {
    pending: Clock,
    paid: CheckCircle,
    shipped: Truck,
    fulfilled: Package,
  };
  const Icon = icons[status] || Clock;
  return (
    <span className={`inline-flex items-center gap-1 font-ui text-[9px] tracking-[0.18em] uppercase px-2 py-1 ${styles[status] || styles.pending}`} data-testid={`mc-order-status-${status}`}>
      <Icon size={10} /> {status}
    </span>
  );
}

/* ─── Order Detail Modal ─── */
function OrderDetailModal({ order, onClose, onSaved }) {
  const [status, setStatus] = useState(order.status || "pending");
  const [tracking, setTracking] = useState(order.tracking || "");
  const [notes, setNotes] = useState(order.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await http.patch(`/marker-collection/admin/orders/${order.id}`, { status, tracking, notes });
      toast.success("Order updated");
      onSaved();
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const addr = order.shipping_address || {};

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 bg-black/80 overflow-y-auto" data-testid="mc-order-modal">
      <div className="bg-[#111] border border-white/10 w-full max-w-2xl mx-4 mb-10 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="font-ui text-[11px] tracking-[0.22em] uppercase text-[#C9A055]">
            Order Details
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="mc-order-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Customer & Date */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] mb-1">CUSTOMER</div>
              <div className="text-[#F5F2EB] text-sm">{order.customer_name}</div>
              <div className="text-[#8a857d] text-xs">{order.customer_email}</div>
            </div>
            <div>
              <div className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] mb-1">PLACED</div>
              <div className="text-[#F5F2EB] text-sm">{new Date(order.created_at).toLocaleString()}</div>
              <div className="text-[#8a857d] text-xs">Stripe: {order.stripe_session_id?.slice(0, 24) || "—"}...</div>
            </div>
          </div>

          {/* Artwork */}
          <div className="border border-white/10 p-4 flex items-center gap-4">
            <Package size={20} className="text-[#C9A055] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[#F5F2EB] text-sm font-medium">{order.artwork_title}</div>
              <div className="text-[#8a857d] text-xs">{order.art_type}{order.quantity > 1 ? ` x ${order.quantity}` : ""}</div>
            </div>
            <div className="text-right">
              <div className="text-[#C9A055] font-display text-lg">${(order.price || 0).toFixed(2)}</div>
              {order.quantity > 1 && order.unit_price && (
                <div className="text-[#8a857d] text-[10px]">${Number(order.unit_price).toFixed(2)} each</div>
              )}
              {order.shipping_cost > 0 && (
                <div className="text-[#8a857d] text-[10px]">incl. ${Number(order.shipping_cost).toFixed(2)} shipping</div>
              )}
            </div>
          </div>

          {/* Delivery Method */}
          <div className="flex items-center gap-2 px-1">
            <span className={`text-[9px] tracking-[0.2em] uppercase font-bold px-2.5 py-1 ${order.delivery_method === "pickup" ? "bg-blue-900/20 text-blue-400" : "bg-white/5 text-[#8a857d]"}`}>
              {order.delivery_method === "pickup" ? "PICKUP / MEET-UP" : "SHIP"}
            </span>
            {order.shipping_cost > 0 && <span className="text-[9px] text-[#8a857d]">+${Number(order.shipping_cost).toFixed(2)} shipping</span>}
            {order.delivery_method === "ship" && (!order.shipping_cost || order.shipping_cost === 0) && <span className="text-[9px] text-emerald-400">FREE SHIPPING</span>}
          </div>

          {/* Shipping Address */}
          {order.delivery_method !== "pickup" && addr.line1 && (
            <div className="border border-white/10 p-4">
              <div className="font-ui text-[9px] tracking-[0.32em] text-[#C9A055] mb-2">SHIPPING ADDRESS</div>
              <div className="text-[#F5F2EB] text-sm">{addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}</div>
              <div className="text-[#B5B0A7] text-sm">{addr.city}, {addr.state} {addr.zip}</div>
            </div>
          )}

          {/* Status & Tracking */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">STATUS</label>
              <select className="stc-input w-full text-[12px]" value={status} onChange={e => setStatus(e.target.value)} data-testid="mc-order-status-select">
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="shipped">Shipped</option>
                <option value="fulfilled">Fulfilled</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">TRACKING NUMBER</label>
              <input className="stc-input w-full text-[12px] font-mono" value={tracking} onChange={e => setTracking(e.target.value)} placeholder="USPS / UPS / FedEx" data-testid="mc-order-tracking" />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">INTERNAL NOTES</label>
            <textarea className="stc-input w-full text-[12px] min-h-[60px]" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes about this order..." data-testid="mc-order-notes" />
          </div>

          {status === "fulfilled" && order.art_type === "Original" && (
            <div className="border border-emerald-700/30 bg-emerald-900/10 p-3 text-emerald-400 text-xs font-ui tracking-wider">
              Marking as fulfilled will automatically mark the original artwork as "Sold"
            </div>
          )}

          {/* ─── Shippo Shipping Label ─── */}
          <OrderShippingActions
            order={order}
            apiPrefix="/marker-collection/admin/orders"
            onLabelPurchased={onSaved}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-white/10">
          <button onClick={onClose} className="btn-outline text-xs px-4 py-2" data-testid="mc-order-cancel">Cancel</button>
          <button onClick={save} disabled={saving}
            className="bg-[#C9A055] text-black px-6 py-2 font-ui text-[10px] tracking-[0.22em] uppercase flex items-center gap-2 hover:bg-[#b8915a] transition-colors disabled:opacity-50"
            data-testid="mc-order-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}


/* ─── Admin Commissions Panel with Add ─── */
function AdminCommissionsPanel({ commissions, commissionStats, onViewCommission, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", description: "", budget: "", status: "accepted" });
  const [adding, setAdding] = useState(false);

  const addCommission = async () => {
    if (!addForm.name.trim() || !addForm.description.trim()) {
      toast.error("Name and description are required");
      return;
    }
    setAdding(true);
    try {
      await http.post("/marker-collection/admin/commissions", {
        name: addForm.name.trim(),
        description: addForm.description.trim(),
        budget: addForm.budget.trim(),
        status: addForm.status,
      });
      toast.success("Project added as commission");
      setAddForm({ name: "", description: "", budget: "", status: "accepted" });
      setShowAdd(false);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div data-testid="mc-commissions-panel">
      {/* Stats + Add button row */}
      <div className="flex items-start justify-between mb-4">
        {commissionStats && (
          <div className="grid grid-cols-2 gap-3 flex-1 mr-4">
            {[
              ["Total Requests", commissionStats.total, "text-[#F5F2EB]"],
              ["New / Unread", commissionStats.new, "text-amber-400"],
            ].map(([label, val, color]) => (
              <div key={label} className="border border-white/10 bg-[#0a0a0a] p-4 text-center">
                <div className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] uppercase mb-1">{label}</div>
                <div className={`font-display text-xl ${color}`}>{val}</div>
              </div>
            ))}
          </div>
        )}
        <button onClick={() => setShowAdd(!showAdd)}
          className="bg-[#C9A055] text-black px-4 py-2 font-ui text-[10px] tracking-[0.22em] uppercase flex items-center gap-2 hover:bg-[#b8915a] transition-colors flex-shrink-0 mt-1"
          data-testid="mc-commission-add-btn">
          <Plus size={14} /> Add My Own
        </button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="border border-[#C9A055]/30 bg-[#0a0a0a] p-4 mb-5 space-y-3" data-testid="mc-commission-add-form">
          <div className="font-ui text-[9px] tracking-[0.32em] text-[#C9A055] uppercase mb-2">Add Your Own Project</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="font-ui text-[9px] tracking-[0.2em] text-[#8a857d] block mb-1">Project Name *</label>
              <input className="stc-input w-full text-[12px]" placeholder="e.g. Nashville Skyline Commission"
                value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))}
                data-testid="mc-commission-add-name" />
            </div>
            <div>
              <label className="font-ui text-[9px] tracking-[0.2em] text-[#8a857d] block mb-1">Budget / Price</label>
              <input className="stc-input w-full text-[12px]" placeholder="e.g. $500"
                value={addForm.budget} onChange={e => setAddForm(p => ({ ...p, budget: e.target.value }))}
                data-testid="mc-commission-add-budget" />
            </div>
          </div>
          <div>
            <label className="font-ui text-[9px] tracking-[0.2em] text-[#8a857d] block mb-1">Description *</label>
            <textarea className="stc-input w-full text-[12px] min-h-[70px]" placeholder="Describe the project..."
              value={addForm.description} onChange={e => setAddForm(p => ({ ...p, description: e.target.value }))}
              data-testid="mc-commission-add-desc" />
          </div>
          <div className="flex items-center gap-3">
            <select className="stc-input text-[12px]" value={addForm.status}
              onChange={e => setAddForm(p => ({ ...p, status: e.target.value }))}
              data-testid="mc-commission-add-status">
              <option value="accepted">Accepted (adds to queue)</option>
              <option value="in_progress">In Progress</option>
              <option value="new">New</option>
            </select>
            <button onClick={addCommission} disabled={adding}
              className="bg-[#C9A055] text-black px-5 py-2 font-ui text-[10px] tracking-[0.22em] uppercase hover:bg-[#b8915a] disabled:opacity-50 transition-colors flex items-center gap-2"
              data-testid="mc-commission-add-submit">
              {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Add Project
            </button>
            <button onClick={() => setShowAdd(false)} className="text-gray-500 hover:text-white font-ui text-[10px] tracking-[0.22em] uppercase">Cancel</button>
          </div>
        </div>
      )}

      {/* Commission list */}
      {commissions.length === 0 ? (
        <div className="border-2 border-dashed border-white/10 text-center py-12">
          <Palette size={24} className="mx-auto mb-3 text-[#8a857d]" />
          <p className="text-[#8a857d] text-sm">No commission requests yet.</p>
          <p className="text-[#8a857d] text-xs mt-2">Add your own projects or wait for customer requests.</p>
        </div>
      ) : (
        <div className="border border-white/10 bg-[#0a0a0a]">
          <div className="grid grid-cols-5 px-4 py-3 border-b border-white/10 font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">
            <div>Date</div><div>Name</div><div>Source</div><div>Budget</div><div>Status</div>
          </div>
          {commissions.map(c => (
            <div key={c.id} onClick={() => onViewCommission(c)}
              className="grid grid-cols-5 px-4 py-3 border-b border-white/5 items-center text-sm cursor-pointer hover:bg-white/5"
              data-testid={`mc-commission-row-${c.id}`}>
              <div className="font-mono text-[#B5B0A7] text-xs">{new Date(c.created_at).toLocaleDateString()}</div>
              <div className="text-[#F4F1EA] text-xs truncate">{c.name}</div>
              <div>
                <span className={`font-ui text-[9px] tracking-[0.18em] uppercase px-2 py-0.5 ${
                  c.source === "admin" ? "text-[#C9A055] bg-[#C9A055]/10" : "text-blue-400 bg-blue-900/20"
                }`}>
                  {c.source === "admin" ? "My Project" : "Customer"}
                </span>
              </div>
              <div className="text-[#C9A055] text-xs">{c.budget || "—"}</div>
              <div>
                <span className={`inline-flex items-center gap-1 font-ui text-[9px] tracking-[0.18em] uppercase px-2 py-1 ${
                  c.status === "new" ? "text-amber-400 bg-amber-900/20"
                    : c.status === "replied" ? "text-blue-400 bg-blue-900/20"
                    : c.status === "accepted" ? "text-emerald-400 bg-emerald-900/20"
                    : c.status === "in_progress" ? "text-[#C9A055] bg-[#C9A055]/10"
                    : c.status === "completed" ? "text-emerald-300 bg-emerald-900/30"
                    : c.status === "declined" ? "text-red-400 bg-red-900/20"
                    : "text-gray-400 bg-gray-900/20"
                }`}>
                  {c.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════
   Market Orders Panel
═══════════════════════════════════════ */
function MarketOrdersPanel({ orders, onRefresh }) {
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const statusColors = {
    pending: { bg: "bg-amber-900/20", text: "text-amber-400" },
    confirmed: { bg: "bg-blue-900/20", text: "text-blue-400" },
    paid: { bg: "bg-emerald-900/20", text: "text-emerald-400" },
    shipped: { bg: "bg-purple-900/20", text: "text-purple-400" },
    delivered: { bg: "bg-emerald-900/20", text: "text-emerald-300" },
    cancelled: { bg: "bg-red-900/20", text: "text-red-400" },
  };
  const statusFlow = ["pending", "confirmed", "paid", "shipped", "delivered", "cancelled"];
  const filtered = filter === "all" ? orders : orders.filter(o => o.status === filter);
  const pendingCount = orders.filter(o => o.status === "pending").length;
  const paidCount = orders.filter(o => o.status === "paid").length;

  const updateStatus = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      await http.patch(`/marker-collection/admin/market-orders/${id}`, { status: newStatus });
      toast.success(`Order updated to ${newStatus}`);
      onRefresh();
    } catch { toast.error("Update failed"); }
    finally { setUpdatingId(null); }
  };

  const updateNotes = async (id, notes) => {
    try {
      await http.patch(`/marker-collection/admin/market-orders/${id}`, { notes });
      toast.success("Notes saved");
      onRefresh();
    } catch { toast.error("Save failed"); }
  };

  return (
    <div data-testid="market-orders-panel">
      {/* Stats bar */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] uppercase">
          {orders.length} total · {pendingCount} pending · {paidCount} paid
        </div>
        <div className="flex gap-1.5 ml-auto flex-wrap">
          {["all", ...statusFlow].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`font-ui text-[9px] tracking-[0.15em] uppercase px-3 py-1.5 border transition-colors ${
                filter === s ? "bg-[#C9A055] text-black border-[#C9A055]" : "border-white/10 text-[#8a857d] hover:border-[#C9A055]"
              }`} data-testid={`mo-filter-${s}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="border-2 border-dashed border-white/10 text-center py-12">
          <ShoppingBag size={24} className="mx-auto mb-3 text-[#8a857d]" />
          <p className="text-[#8a857d] text-sm">No {filter === "all" ? "" : filter} orders yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(order => {
            const expanded = expandedId === order.id;
            const sc = statusColors[order.status] || statusColors.pending;
            return (
              <div key={order.id} className="border border-white/8 bg-[#0a0a0a]" data-testid={`mo-row-${order.id}`}>
                {/* Summary row */}
                <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpandedId(expanded ? null : order.id)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-bold text-[#F5F2EB] truncate">{order.product_title || "Unknown Product"}</span>
                      {order.vendor_code && <span className="text-[8px] font-mono text-[#6a6660]">{order.vendor_code}</span>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[9px] text-[#8a857d]">{order.customer_name}</span>
                      <span className="text-[8px] text-[#6a6660]">·</span>
                      <span className="text-[9px] text-[#8a857d]">{order.customer_email}</span>
                      <span className="text-[8px] text-[#6a6660]">·</span>
                      <span className="text-[9px] text-[#6a6660]">{new Date(order.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="font-mono text-sm font-bold text-[#C9A055]">${Number(order.total).toFixed(2)}</span>
                    <span className={`text-[8px] tracking-[0.15em] uppercase px-2 py-1 ${sc.bg} ${sc.text}`}>
                      {order.status}
                    </span>
                    <ChevronDown size={14} className={`text-[#6a6660] transition-transform ${expanded ? "rotate-180" : ""}`} />
                  </div>
                </div>

                {/* Expanded detail */}
                {expanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-white/5 space-y-3" data-testid={`mo-detail-${order.id}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
                      <div>
                        <div className="text-[8px] tracking-[0.2em] text-[#6a6660] uppercase mb-0.5">Qty</div>
                        <div className="text-[#F5F2EB]">{order.quantity}</div>
                      </div>
                      <div>
                        <div className="text-[8px] tracking-[0.2em] text-[#6a6660] uppercase mb-0.5">Unit Price</div>
                        <div className="text-[#F5F2EB]">${Number(order.unit_price).toFixed(2)}</div>
                      </div>
                      <div>
                        <div className="text-[8px] tracking-[0.2em] text-[#6a6660] uppercase mb-0.5">Payment</div>
                        <div className="text-[#F5F2EB] capitalize">{order.payment_method}</div>
                      </div>
                      <div>
                        <div className="text-[8px] tracking-[0.2em] text-[#6a6660] uppercase mb-0.5">Ordered</div>
                        <div className="text-[#F5F2EB]">{new Date(order.created_at).toLocaleString()}</div>
                      </div>
                    </div>

                    {/* Status actions */}
                    <div>
                      <div className="text-[8px] tracking-[0.2em] text-[#6a6660] uppercase mb-1.5">Update Status</div>
                      <div className="flex gap-1.5 flex-wrap">
                        {statusFlow.map(s => (
                          <button key={s} onClick={() => updateStatus(order.id, s)}
                            disabled={updatingId === order.id || order.status === s}
                            className={`text-[9px] tracking-[0.12em] uppercase px-3 py-1.5 border transition-colors disabled:opacity-30 ${
                              order.status === s ? "bg-[#C9A055] text-black border-[#C9A055]" : "border-white/10 text-[#8a857d] hover:border-[#C9A055]"
                            }`} data-testid={`mo-status-${s}-${order.id}`}>
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <div className="text-[8px] tracking-[0.2em] text-[#6a6660] uppercase mb-1">Admin Notes</div>
                      <div className="flex gap-2">
                        <input className="stc-input flex-1 text-[12px]" defaultValue={order.notes || ""}
                          id={`mo-notes-${order.id}`} placeholder="Internal notes..." data-testid={`mo-notes-${order.id}`} />
                        <button onClick={() => updateNotes(order.id, document.getElementById(`mo-notes-${order.id}`).value)}
                          className="px-3 py-1.5 bg-[#C9A055] text-black text-[9px] tracking-[0.15em] uppercase font-bold hover:bg-[#b8915a] transition-colors"
                          data-testid={`mo-save-notes-${order.id}`}>
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════
   Name Winners Panel
═══════════════════════════════════════ */
function NameWinnersPanel({ winners, onRefresh }) {
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const statusColors = {
    new: { bg: "bg-amber-900/20", text: "text-amber-400" },
    contacted: { bg: "bg-blue-900/20", text: "text-blue-400" },
    shipped: { bg: "bg-purple-900/20", text: "text-purple-400" },
    hand_delivered: { bg: "bg-emerald-900/20", text: "text-emerald-400" },
    complete: { bg: "bg-emerald-900/20", text: "text-emerald-300" },
  };
  const statusFlow = ["new", "contacted", "shipped", "hand_delivered", "complete"];
  const filtered = filter === "all" ? winners : winners.filter(w => w.status === filter);
  const newCount = winners.filter(w => w.status === "new").length;

  const updateStatus = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      await http.patch(`/marker-collection/admin/name-winners/${id}`, { status: newStatus });
      toast.success(`Updated to ${newStatus.replace("_", " ")}`);
      onRefresh();
    } catch { toast.error("Update failed"); }
    finally { setUpdatingId(null); }
  };

  const updateNotes = async (id, notes) => {
    try {
      await http.patch(`/marker-collection/admin/name-winners/${id}`, { notes });
      toast.success("Notes saved");
      onRefresh();
    } catch { toast.error("Save failed"); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/name-winner`);
    toast.success("Link copied! Share it with the winner.");
  };

  return (
    <div data-testid="name-winners-panel">
      {/* Header */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] uppercase">
          {winners.length} submissions · {newCount} new
        </div>
        <button onClick={copyLink}
          className="ml-auto bg-[#C9A055] text-black px-4 py-2 font-ui text-[10px] tracking-[0.22em] uppercase flex items-center gap-2 hover:bg-[#b8915a] transition-colors"
          data-testid="nw-copy-link">
          <Link2 size={14} /> Copy Winner Page Link
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-1.5 mb-4 flex-wrap">
        {["all", ...statusFlow].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`font-ui text-[9px] tracking-[0.15em] uppercase px-3 py-1.5 border transition-colors ${
              filter === s ? "bg-[#C9A055] text-black border-[#C9A055]" : "border-white/10 text-[#8a857d] hover:border-[#C9A055]"
            }`} data-testid={`nw-filter-${s}`}>
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="border-2 border-dashed border-white/10 text-center py-12">
          <Award size={24} className="mx-auto mb-3 text-[#8a857d]" />
          <p className="text-[#8a857d] text-sm">No {filter === "all" ? "" : filter.replace("_", " ")} submissions.</p>
          <p className="text-[#6a6660] text-xs mt-1">Share the winner page link with someone who named a print!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(w => {
            const expanded = expandedId === w.id;
            const sc = statusColors[w.status] || statusColors.new;
            return (
              <div key={w.id} className="border border-white/8 bg-[#0a0a0a]" data-testid={`nw-row-${w.id}`}>
                {/* Summary */}
                <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpandedId(expanded ? null : w.id)}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-bold text-[#F5F2EB]">{w.name}</span>
                      <span className="text-[8px] text-[#6a6660]">·</span>
                      <span className="text-xs text-[#C9A055] font-bold italic truncate">"{w.artwork_name}"</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] text-[#6a6660]">{new Date(w.created_at).toLocaleDateString()}</span>
                      {w.phone && <><span className="text-[8px] text-[#6a6660]">·</span><span className="text-[9px] text-[#8a857d]">{w.phone}</span></>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-[8px] tracking-[0.15em] uppercase px-2 py-1 ${sc.bg} ${sc.text}`}>
                      {w.status.replace("_", " ")}
                    </span>
                    <ChevronDown size={14} className={`text-[#6a6660] transition-transform ${expanded ? "rotate-180" : ""}`} />
                  </div>
                </div>

                {/* Expanded */}
                {expanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-white/5 space-y-3" data-testid={`nw-detail-${w.id}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px]">
                      <div>
                        <div className="text-[8px] tracking-[0.2em] text-[#6a6660] uppercase mb-0.5">Winner Name</div>
                        <div className="text-[#F5F2EB]">{w.name}</div>
                      </div>
                      <div>
                        <div className="text-[8px] tracking-[0.2em] text-[#6a6660] uppercase mb-0.5">Artwork Name Chosen</div>
                        <div className="text-[#C9A055] font-bold italic">"{w.artwork_name}"</div>
                      </div>
                      {w.phone && (
                        <div>
                          <div className="text-[8px] tracking-[0.2em] text-[#6a6660] uppercase mb-0.5">Phone</div>
                          <div className="text-[#F5F2EB]">{w.phone}</div>
                        </div>
                      )}
                      {w.address && (
                        <div>
                          <div className="text-[8px] tracking-[0.2em] text-[#6a6660] uppercase mb-0.5">Mailing Address</div>
                          <div className="text-[#F5F2EB] whitespace-pre-line">{w.address}</div>
                        </div>
                      )}
                      {w.message && (
                        <div className="sm:col-span-2">
                          <div className="text-[8px] tracking-[0.2em] text-[#6a6660] uppercase mb-0.5">Message</div>
                          <div className="text-[#F5F2EB] italic">"{w.message}"</div>
                        </div>
                      )}
                      <div>
                        <div className="text-[8px] tracking-[0.2em] text-[#6a6660] uppercase mb-0.5">Submitted</div>
                        <div className="text-[#F5F2EB]">{new Date(w.created_at).toLocaleString()}</div>
                      </div>
                    </div>

                    {/* Status actions */}
                    <div>
                      <div className="text-[8px] tracking-[0.2em] text-[#6a6660] uppercase mb-1.5">Update Status</div>
                      <div className="flex gap-1.5 flex-wrap">
                        {statusFlow.map(s => (
                          <button key={s} onClick={() => updateStatus(w.id, s)}
                            disabled={updatingId === w.id || w.status === s}
                            className={`text-[9px] tracking-[0.12em] uppercase px-3 py-1.5 border transition-colors disabled:opacity-30 ${
                              w.status === s ? "bg-[#C9A055] text-black border-[#C9A055]" : "border-white/10 text-[#8a857d] hover:border-[#C9A055]"
                            }`} data-testid={`nw-status-${s}-${w.id}`}>
                            {s.replace("_", " ")}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <div className="text-[8px] tracking-[0.2em] text-[#6a6660] uppercase mb-1">Admin Notes</div>
                      <div className="flex gap-2">
                        <input className="stc-input flex-1 text-[12px]" defaultValue={w.notes || ""}
                          id={`nw-notes-${w.id}`} placeholder="Internal notes..." data-testid={`nw-notes-${w.id}`} />
                        <button onClick={() => updateNotes(w.id, document.getElementById(`nw-notes-${w.id}`).value)}
                          className="px-3 py-1.5 bg-[#C9A055] text-black text-[9px] tracking-[0.15em] uppercase font-bold hover:bg-[#b8915a] transition-colors"
                          data-testid={`nw-save-notes-${w.id}`}>
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* ─── Commission Detail Modal ─── */
function CommissionDetailModal({ commission, onClose, onSaved }) {
  const [status, setStatus] = useState(commission.status || "new");
  const [notes, setNotes] = useState(commission.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await http.patch(`/marker-collection/admin/commissions/${commission.id}`, { status, notes });
      toast.success("Commission updated");
      onSaved();
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 bg-black/80 overflow-y-auto" data-testid="mc-commission-modal">
      <div className="bg-[#111] border border-white/10 w-full max-w-2xl mx-4 mb-10 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="font-ui text-[11px] tracking-[0.22em] uppercase text-[#C9A055]">Commission Request</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="mc-commission-detail-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <div className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] mb-1">CUSTOMER</div>
              <div className="text-[#F5F2EB] text-sm">{commission.name}</div>
              <a href={`mailto:${commission.email}`} className="text-[#C9A055] text-xs hover:underline">{commission.email}</a>
            </div>
            <div>
              <div className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] mb-1">SUBMITTED</div>
              <div className="text-[#F5F2EB] text-sm">{new Date(commission.created_at).toLocaleString()}</div>
              <div className="text-[#8a857d] text-xs mt-1">Budget: <span className="text-[#C9A055]">{commission.budget || "Not specified"}</span></div>
            </div>
          </div>

          <div className="border border-white/10 p-4">
            <div className="font-ui text-[9px] tracking-[0.32em] text-[#C9A055] mb-2">THEIR VISION</div>
            <p className="text-[#B5B0A7] text-sm leading-relaxed whitespace-pre-wrap">{commission.description}</p>
          </div>

          {(commission.reference_images || []).length > 0 && (
            <div>
              <div className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] mb-2">REFERENCE IMAGES</div>
              <div className="flex flex-wrap gap-2">
                {commission.reference_images.map((img, i) => (
                  <a key={i} href={img.startsWith("http") ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`} target="_blank" rel="noopener noreferrer"
                    className="block w-24 h-24 border border-white/10 overflow-hidden hover:border-[#C9A055] transition-colors">
                    <img src={img.startsWith("http") ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`} alt={`Ref ${i + 1}`} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">STATUS</label>
              <select className="stc-input w-full text-[12px]" value={status} onChange={e => setStatus(e.target.value)} data-testid="mc-commission-status-select">
                <option value="new">New</option>
                <option value="replied">Replied</option>
                <option value="accepted">Accepted</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="declined">Declined</option>
              </select>
            </div>
          </div>

          <div>
            <label className="font-ui text-[9px] tracking-[0.32em] text-[#8a857d] block mb-1">INTERNAL NOTES</label>
            <textarea className="stc-input w-full text-[12px] min-h-[80px]" value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Notes about this commission..." data-testid="mc-commission-notes" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-white/10">
          <button onClick={onClose} className="btn-outline text-xs px-4 py-2" data-testid="mc-commission-cancel">Cancel</button>
          <button onClick={save} disabled={saving}
            className="bg-[#C9A055] text-black px-6 py-2 font-ui text-[10px] tracking-[0.22em] uppercase flex items-center gap-2 hover:bg-[#b8915a] transition-colors disabled:opacity-50"
            data-testid="mc-commission-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════
   Charity Applications Panel
═══════════════════════════════════════ */
function CharityAppsPanel({ apps, onRefresh }) {
  const updateStatus = async (id, status) => {
    try {
      await http.patch(`/marker-collection/admin/charity/applications/${id}`, { status });
      toast.success(`Application ${status}`);
      onRefresh();
    } catch { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this application?")) return;
    try {
      await http.delete(`/marker-collection/admin/charity/applications/${id}`);
      toast.success("Deleted");
      onRefresh();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="mc-charity-apps-panel">
      <p className="font-ui text-[11px] text-[#8a857d]">
        Nonprofits that applied through the "Art for a Cause" section on the collection page.
      </p>
      {apps.length === 0 ? (
        <div className="border border-white/10 bg-[#0a0a0a] p-8 text-center text-[#6a6660] font-body text-sm">No applications yet.</div>
      ) : (
        <div className="space-y-3">
          {apps.map(a => (
            <div key={a.id} className="border border-white/10 bg-[#0a0a0a] p-4" data-testid={`charity-app-${a.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-display text-[#F5F2EB] text-lg">{a.organization_name}</span>
                    <span className={`font-ui text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 border ${
                      a.status === "approved" ? "border-emerald-400/40 text-emerald-400" :
                      a.status === "declined" ? "border-red-400/40 text-red-400" :
                      "border-[#C9A055]/40 text-[#C9A055]"
                    }`}>{a.status}</span>
                  </div>
                  <div className="text-[#8a857d] text-xs mb-2">
                    {a.contact_name} · <a href={`mailto:${a.contact_email}`} className="text-[#C9A055] hover:underline">{a.contact_email}</a>
                    {a.website && <> · <a href={a.website.startsWith("http") ? a.website : `https://${a.website}`} target="_blank" rel="noreferrer" className="text-[#C9A055] hover:underline">{a.website}</a></>}
                  </div>
                  <p className="font-body text-[13px] text-[#B5B0A7] whitespace-pre-line">{a.mission}</p>
                  <div className="text-[#6a6660] text-[10px] mt-2">{new Date(a.created_at).toLocaleString()}</div>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  {a.status !== "approved" && (
                    <button onClick={() => updateStatus(a.id, "approved")}
                      className="font-ui text-[9px] tracking-wider text-emerald-400 border border-emerald-400/30 px-3 py-1.5 hover:bg-emerald-400/10"
                      data-testid={`charity-approve-${a.id}`}>Approve</button>
                  )}
                  {a.status !== "declined" && (
                    <button onClick={() => updateStatus(a.id, "declined")}
                      className="font-ui text-[9px] tracking-wider text-red-400 border border-red-400/30 px-3 py-1.5 hover:bg-red-400/10"
                      data-testid={`charity-decline-${a.id}`}>Decline</button>
                  )}
                  <button onClick={() => remove(a.id)}
                    className="text-[#6a6660] hover:text-red-400 px-1.5" data-testid={`charity-delete-${a.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ════════════════════════════════════════════════════════
   SUGGESTIONS PANEL (Admin)
════════════════════════════════════════════════════════ */
function SuggestionsPanel({ suggestions, onRefresh }) {
  const [filter, setFilter] = useState("all");

  const updateStatus = async (id, status) => {
    try {
      await http.patch(`/marker-collection/admin/suggestions/${id}`, { status });
      toast.success(`Suggestion ${status.replace("_", " ")}`);
      onRefresh();
    } catch { toast.error("Failed to update"); }
  };

  const addNote = async (id) => {
    const note = prompt("Add a note (shown publicly):");
    if (note === null) return;
    try {
      await http.patch(`/marker-collection/admin/suggestions/${id}`, { admin_note: note });
      toast.success("Note added");
      onRefresh();
    } catch { toast.error("Failed"); }
  };

  const moveToQueue = async (id) => {
    try {
      await http.post(`/marker-collection/admin/suggestions/${id}/to-queue`);
      toast.success("Added to commission queue!");
      onRefresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const deleteSuggestion = async (id) => {
    if (!window.confirm("Delete this suggestion?")) return;
    try {
      await http.delete(`/marker-collection/admin/suggestions/${id}`);
      toast.success("Deleted");
      onRefresh();
    } catch { toast.error("Failed"); }
  };

  const filtered = filter === "all" ? suggestions
    : filter === "pending" ? suggestions.filter(s => s.status === "pending")
    : filter === "accepted" ? suggestions.filter(s => ["accepted_commission", "accepted_own", "in_queue"].includes(s.status))
    : filter === "denied" ? suggestions.filter(s => s.status === "denied")
    : suggestions;

  const STATUS_COLORS = {
    pending: "text-[#8a857d]", accepted_commission: "text-emerald-400", accepted_own: "text-emerald-400",
    denied: "text-red-400", in_queue: "text-[#C9A055]", completed: "text-emerald-400",
  };

  return (
    <div className="space-y-4" data-testid="mc-suggestions-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055] uppercase">
          Drawing Suggestions ({suggestions.length})
        </div>
        <div className="flex gap-2">
          {["all", "pending", "accepted", "denied"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`font-ui text-[9px] tracking-[0.15em] uppercase px-3 py-1.5 border transition-colors ${
                filter === f ? "bg-[#C9A055] text-black border-[#C9A055]" : "bg-transparent text-[#8a857d] border-white/10 hover:border-[#C9A055]"
              }`} data-testid={`sg-filter-${f}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-8 text-[#6a6660] font-ui text-xs">No suggestions found.</div>
      ) : (
        <div className="space-y-2">
          {filtered.sort((a, b) => (b.votes || 0) - (a.votes || 0)).map(s => (
            <div key={s.id} className="border border-white/10 bg-white/[0.02] p-4" data-testid={`sg-item-${s.id}`}>
              <div className="flex items-start gap-4">
                {/* Vote count */}
                <div className="flex flex-col items-center gap-0.5 flex-shrink-0 pt-1">
                  <ChevronUp size={14} className="text-[#C9A055]" />
                  <span className="font-display text-lg text-[#C9A055]">{s.votes || 0}</span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#F5F2EB] font-medium">{s.text}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <span className={`text-[9px] tracking-wider uppercase font-bold ${STATUS_COLORS[s.status] || "text-[#8a857d]"}`}>
                      {s.status?.replace(/_/g, " ") || "PENDING"}
                    </span>
                    <span className="text-[9px] text-[#6a6660]">{s.category}</span>
                    <span className="text-[9px] text-[#6a6660]">by {s.submitter_name}</span>
                    <span className="text-[9px] text-[#6a6660]">{new Date(s.created_at).toLocaleDateString()}</span>
                  </div>
                  {s.admin_note && (
                    <p className="mt-1.5 text-[10px] italic text-[#C9A055]">Note: "{s.admin_note}"</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                  {s.status === "pending" && (
                    <>
                      <button onClick={() => updateStatus(s.id, "accepted_commission")}
                        className="font-ui text-[8px] tracking-wider uppercase px-3 py-1.5 bg-emerald-900/30 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-900/50 transition-colors"
                        title="Accept as commission (credits suggester)"
                        data-testid={`sg-accept-commission-${s.id}`}>
                        Commission
                      </button>
                      <button onClick={() => updateStatus(s.id, "accepted_own")}
                        className="font-ui text-[8px] tracking-wider uppercase px-3 py-1.5 bg-[#C9A055]/10 text-[#C9A055] border border-[#C9A055]/20 hover:bg-[#C9A055]/20 transition-colors"
                        title="Accept as your own (inspired by suggestion)"
                        data-testid={`sg-accept-own-${s.id}`}>
                        My Own
                      </button>
                      <button onClick={() => updateStatus(s.id, "denied")}
                        className="font-ui text-[8px] tracking-wider uppercase px-3 py-1.5 bg-red-900/20 text-red-400 border border-red-500/20 hover:bg-red-900/40 transition-colors"
                        data-testid={`sg-deny-${s.id}`}>
                        Deny
                      </button>
                    </>
                  )}
                  {["accepted_commission", "accepted_own"].includes(s.status) && !s.commission_queue_id && (
                    <button onClick={() => moveToQueue(s.id)}
                      className="font-ui text-[8px] tracking-wider uppercase px-3 py-1.5 bg-blue-900/20 text-blue-400 border border-blue-500/20 hover:bg-blue-900/40 transition-colors"
                      data-testid={`sg-to-queue-${s.id}`}>
                      Move to Queue
                    </button>
                  )}
                  {s.commission_queue_id && (
                    <span className="font-ui text-[8px] tracking-wider uppercase px-3 py-1.5 bg-[#C9A055]/10 text-[#C9A055]">
                      In Queue
                    </span>
                  )}
                  <button onClick={() => addNote(s.id)}
                    className="text-[#6a6660] hover:text-[#C9A055] px-1" title="Add note"
                    data-testid={`sg-note-${s.id}`}>
                    <MessageSquare size={13} />
                  </button>
                  <button onClick={() => deleteSuggestion(s.id)}
                    className="text-[#6a6660] hover:text-red-400 px-1" title="Delete"
                    data-testid={`sg-delete-${s.id}`}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function RegistryPanel({ artworks, onRefresh }) {
  const [entries, setEntries] = useState([]);
  const [registryArtworks, setRegistryArtworks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | artwork_id
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | claimed | unclaimed | transferred
  const [showAdd, setShowAdd] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [saving, setSaving] = useState(false);

  // Add/Edit form
  const [formArtwork, setFormArtwork] = useState("");
  const [formEdition, setFormEdition] = useState("");
  const [formName, setFormName] = useState("");
  const [formCity, setFormCity] = useState("");
  const [formState, setFormState] = useState("");
  const [formDate, setFormDate] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [formTransferNote, setFormTransferNote] = useState("");

  const loadData = useCallback(async () => {
    try {
      const query = filter !== "all" ? `?artwork_id=${filter}` : "";
      const [eRes, aRes, sRes] = await Promise.all([
        http.get(`/marker-collection/admin/registry${query}`),
        http.get("/marker-collection/admin/registry/artworks"),
        http.get("/marker-collection/admin/registry/stats"),
      ]);
      setEntries(eRes.data || []);
      setRegistryArtworks(aRes.data || []);
      setStats(sRes.data);
    } catch { toast.error("Failed to load registry"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { loadData(); }, [loadData]);

  const resetForm = () => {
    setFormArtwork(""); setFormEdition(""); setFormName(""); setFormCity("");
    setFormState(""); setFormDate(""); setFormNotes(""); setFormTransferNote("");
    setShowAdd(false); setEditEntry(null);
  };

  const openEdit = (entry) => {
    setEditEntry(entry);
    setFormName(entry.owner_name || "");
    setFormCity(entry.owner_city || "");
    setFormState(entry.owner_state || "");
    setFormDate(entry.purchase_date ? entry.purchase_date.split("T")[0] : "");
    setFormNotes(entry.notes || "");
    setFormEdition(String(entry.edition_number || ""));
    setFormTransferNote("");
    setShowAdd(false);
  };

  const openAdd = () => {
    resetForm();
    setShowAdd(true);
    if (filter !== "all") setFormArtwork(filter);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editEntry) {
        await http.patch(`/marker-collection/admin/registry/${editEntry.id}`, {
          owner_name: formName, owner_city: formCity, owner_state: formState,
          purchase_date: formDate || undefined, notes: formNotes,
          edition_number: formEdition ? Number(formEdition) : undefined,
          transfer_note: formTransferNote || undefined,
        });
        toast.success(formTransferNote ? "Transfer recorded" : "Entry updated");
      } else {
        if (!formArtwork) { toast.error("Select an artwork"); setSaving(false); return; }
        await http.post("/marker-collection/admin/registry", {
          artwork_id: formArtwork, edition_number: formEdition ? Number(formEdition) : undefined,
          owner_name: formName, owner_city: formCity, owner_state: formState,
          purchase_date: formDate || undefined, notes: formNotes,
        });
        toast.success("Owner registered");
      }
      resetForm(); loadData(); onRefresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleUnclaim = async (entry) => {
    if (!confirm(`Remove ${entry.owner_name || "owner"} from #${entry.edition_number}?`)) return;
    try {
      await http.delete(`/marker-collection/admin/registry/${entry.id}`);
      toast.success("Slot unclaimed"); loadData(); onRefresh();
    } catch { toast.error("Failed to unclaim"); }
  };

  const handleInitArtwork = async (artworkId) => {
    try {
      const { data } = await http.post(`/marker-collection/admin/registry/init/${artworkId}`);
      toast.success(`${data.slots_created} slots initialized`); loadData(); onRefresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to init"); }
  };

  const filtered = entries.filter(e => {
    if (statusFilter !== "all" && e.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (e.owner_name || "").toLowerCase().includes(q) ||
        (e.artwork_title || "").toLowerCase().includes(q) ||
        (e.owner_city || "").toLowerCase().includes(q) ||
        (e.owner_state || "").toLowerCase().includes(q) ||
        String(e.edition_number).includes(q);
    }
    return true;
  });

  const STATUS_COLORS = {
    unclaimed: { bg: "rgba(255,255,255,0.05)", text: "#8a857d", border: "rgba(255,255,255,0.1)" },
    claimed: { bg: "rgba(74,222,128,0.08)", text: "#4ade80", border: "rgba(74,222,128,0.25)" },
    transferred: { bg: "rgba(201,160,85,0.08)", text: "#C9A055", border: "rgba(201,160,85,0.25)" },
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-[#C9A055]" /></div>;

  // Artworks without registry entries
  const registeredArtworkIds = new Set(registryArtworks.map(a => a.artwork_id));
  const unregisteredArtworks = artworks.filter(a => !registeredArtworkIds.has(a.id) && !a.linked_parent_id);

  return (
    <div data-testid="mc-registry-panel">
      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="mc-registry-stats">
          {[
            ["Total Slots", stats.total, BookOpen],
            ["Claimed", stats.claimed, Users],
            ["Unclaimed", stats.unclaimed, Hash],
            ["Transferred", stats.transferred, ArrowRight],
          ].map(([label, val, Icon]) => (
            <div key={label} className="p-3 border border-white/10 text-center">
              <Icon size={16} className="mx-auto mb-1 text-[#C9A055]" />
              <p className="text-xl font-black text-[#F5F2EB]" style={{ fontFamily: "'Saira Condensed', sans-serif" }}>{val}</p>
              <p className="font-ui text-[8px] tracking-[0.3em] uppercase text-[#8a857d]">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Unregistered Artworks Alert */}
      {unregisteredArtworks.length > 0 && (
        <div className="mb-4 p-3 border border-[#C9A055]/30 bg-[#C9A055]/5" data-testid="mc-registry-unregistered">
          <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-[#C9A055] mb-2">
            {unregisteredArtworks.length} artwork{unregisteredArtworks.length !== 1 ? "s" : ""} without registry
          </p>
          <div className="flex flex-wrap gap-2">
            {unregisteredArtworks.map(a => (
              <button key={a.id} onClick={() => handleInitArtwork(a.id)}
                className="text-[10px] px-3 py-1.5 border border-[#C9A055]/40 text-[#C9A055] hover:bg-[#C9A055]/10 transition-colors flex items-center gap-1.5"
                data-testid={`mc-registry-init-${a.id}`}>
                <Plus size={10} /> {a.title}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5 items-start sm:items-center justify-between" data-testid="mc-registry-controls">
        <div className="flex gap-2 flex-wrap items-center">
          <select value={filter} onChange={e => setFilter(e.target.value)}
            className="stc-input text-xs py-1.5 pr-8" data-testid="mc-registry-artwork-filter">
            <option value="all">All Artworks</option>
            {registryArtworks.map(a => (
              <option key={a.artwork_id} value={a.artwork_id}>{a.title} ({a.claimed}/{a.total})</option>
            ))}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="stc-input text-xs py-1.5 pr-8" data-testid="mc-registry-status-filter">
            <option value="all">All Status</option>
            <option value="unclaimed">Unclaimed</option>
            <option value="claimed">Claimed</option>
            <option value="transferred">Transferred</option>
          </select>
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8a857d]" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, city..."
              className="stc-input text-xs py-1.5 pl-7 w-44" data-testid="mc-registry-search" />
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={loadData} className="btn-outline text-[10px] flex items-center gap-1.5" data-testid="mc-registry-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
          <button onClick={openAdd} className="btn-gold text-[10px] flex items-center gap-1.5" data-testid="mc-registry-add">
            <Plus size={12} /> Add Owner
          </button>
        </div>
      </div>

      {/* Add / Edit Form */}
      {(showAdd || editEntry) && (
        <div className="mb-5 p-4 border border-[#C9A055]/30 bg-[#C9A055]/5" data-testid="mc-registry-form">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#C9A055]">
              {editEntry ? `EDIT #${editEntry.edition_number} — ${editEntry.artwork_title}` : "REGISTER NEW OWNER"}
            </h3>
            <button onClick={resetForm}><X size={14} className="text-[#8a857d] hover:text-white" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {!editEntry && (
              <div>
                <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">ARTWORK *</label>
                <select value={formArtwork} onChange={e => setFormArtwork(e.target.value)}
                  className="stc-input w-full text-xs" data-testid="mc-registry-form-artwork">
                  <option value="">Select artwork...</option>
                  {registryArtworks.map(a => <option key={a.artwork_id} value={a.artwork_id}>{a.title}</option>)}
                  {unregisteredArtworks.map(a => <option key={a.id} value={a.id}>{a.title} (no registry)</option>)}
                </select>
              </div>
            )}
            {!editEntry && (
              <div>
                <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">EDITION # (auto if empty)</label>
                <input type="number" min="1" value={formEdition} onChange={e => setFormEdition(e.target.value)}
                  className="stc-input w-full text-xs" placeholder="Auto-assign" data-testid="mc-registry-form-edition" />
              </div>
            )}
            <div>
              <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">OWNER NAME *</label>
              <input value={formName} onChange={e => setFormName(e.target.value)}
                className="stc-input w-full text-xs" placeholder="Full name" data-testid="mc-registry-form-name" />
            </div>
            <div>
              <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">CITY</label>
              <input value={formCity} onChange={e => setFormCity(e.target.value)}
                className="stc-input w-full text-xs" placeholder="City" data-testid="mc-registry-form-city" />
            </div>
            <div>
              <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">STATE</label>
              <input value={formState} onChange={e => setFormState(e.target.value)}
                className="stc-input w-full text-xs" placeholder="TN" data-testid="mc-registry-form-state" />
            </div>
            <div>
              <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">PURCHASE DATE</label>
              <input type="date" value={formDate} onChange={e => setFormDate(e.target.value)}
                className="stc-input w-full text-xs" data-testid="mc-registry-form-date" />
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="font-ui text-[9px] tracking-[0.3em] text-[#8a857d] block mb-1">NOTES</label>
              <input value={formNotes} onChange={e => setFormNotes(e.target.value)}
                className="stc-input w-full text-xs" placeholder="In-person sale, gift, etc." data-testid="mc-registry-form-notes" />
            </div>
            {editEntry && editEntry.status !== "unclaimed" && editEntry.owner_name && formName !== editEntry.owner_name && (
              <div className="sm:col-span-2 lg:col-span-3 p-2 border border-[#C9A055]/30 bg-[#C9A055]/5">
                <label className="font-ui text-[9px] tracking-[0.3em] text-[#C9A055] block mb-1">TRANSFER NOTE (name changed — this creates a permanent transfer record)</label>
                <input value={formTransferNote} onChange={e => setFormTransferNote(e.target.value)}
                  className="stc-input w-full text-xs" placeholder="Reason for transfer..." data-testid="mc-registry-form-transfer" />
              </div>
            )}
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={handleSave} disabled={saving}
              className="btn-gold text-[10px] flex items-center gap-1.5 disabled:opacity-50" data-testid="mc-registry-form-save">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              {editEntry ? "SAVE CHANGES" : "REGISTER"}
            </button>
            <button onClick={resetForm} className="btn-outline text-[10px]">CANCEL</button>
          </div>
        </div>
      )}

      {/* Registry Table */}
      <div className="border border-white/10 overflow-x-auto" data-testid="mc-registry-table">
        <table className="w-full min-w-[700px]">
          <thead>
            <tr className="border-b border-white/10 bg-white/[0.02]">
              {["#", "Artwork", "Owner", "Location", "Date", "Method", "Status", ""].map(h => (
                <th key={h} className="font-ui text-[8px] tracking-[0.3em] uppercase text-[#8a857d] text-left px-3 py-2.5">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-10 text-[#8a857d] text-xs">No entries found</td></tr>
            ) : filtered.map(entry => {
              const sc = STATUS_COLORS[entry.status] || STATUS_COLORS.unclaimed;
              const isUnclaimed = entry.status === "unclaimed";
              return (
                <tr key={entry.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                  data-testid={`mc-registry-row-${entry.id}`}>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center justify-center w-8 h-8 text-sm font-black"
                      style={{ fontFamily: "'Saira Condensed', sans-serif", background: isUnclaimed ? "rgba(255,255,255,0.03)" : "rgba(201,160,85,0.12)", color: isUnclaimed ? "#8a857d" : "#C9A055", border: `1px solid ${sc.border}` }}>
                      {entry.edition_number}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <p className="text-xs text-[#F5F2EB] truncate max-w-[160px]">{entry.artwork_title}</p>
                    <p className="text-[9px] text-[#8a857d]">{entry.art_type} · {entry.edition_number} of {entry.edition_size}</p>
                  </td>
                  <td className="px-3 py-2.5">
                    {isUnclaimed ? (
                      <span className="text-[10px] text-[#8a857d] italic">Unclaimed</span>
                    ) : (
                      <p className="text-xs text-[#F5F2EB] font-medium">{entry.owner_name}</p>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {(entry.owner_city || entry.owner_state) ? (
                      <span className="text-[10px] text-[#8a857d] flex items-center gap-1">
                        <MapPin size={9} /> {[entry.owner_city, entry.owner_state].filter(Boolean).join(", ")}
                      </span>
                    ) : <span className="text-[10px] text-[#8a857d]">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="text-[10px] text-[#8a857d]">
                      {entry.purchase_date ? new Date(entry.purchase_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="text-[9px] tracking-[0.15em] uppercase text-[#8a857d]">
                      {entry.purchase_method ? entry.purchase_method.replace("web_", "").replace("_", " ") : "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="inline-block px-2.5 py-1 text-[8px] tracking-[0.2em] uppercase font-bold"
                      style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}` }}>
                      {entry.status}
                    </span>
                    {entry.transfer_history?.length > 0 && (
                      <p className="text-[8px] text-[#C9A055] mt-0.5">{entry.transfer_history.length} transfer{entry.transfer_history.length > 1 ? "s" : ""}</p>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex gap-1.5 justify-end">
                      <button onClick={() => openEdit(entry)} className="p-1.5 hover:bg-white/10 transition-colors" title="Edit"
                        data-testid={`mc-registry-edit-${entry.id}`}>
                        <Edit3 size={12} className="text-[#C9A055]" />
                      </button>
                      {!isUnclaimed && (
                        <button onClick={() => handleUnclaim(entry)} className="p-1.5 hover:bg-red-500/10 transition-colors" title="Unclaim"
                          data-testid={`mc-registry-unclaim-${entry.id}`}>
                          <Trash2 size={12} className="text-red-400" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary by Artwork */}
      {registryArtworks.length > 0 && (
        <div className="mt-6" data-testid="mc-registry-artwork-summary">
          <h4 className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#C9A055] mb-3">OWNERSHIP BY ARTWORK</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {registryArtworks.map(a => {
              const pct = a.total > 0 ? Math.round((a.claimed / a.total) * 100) : 0;
              return (
                <button key={a.artwork_id} onClick={() => setFilter(a.artwork_id)}
                  className={`p-3 border text-left transition-colors hover:border-[#C9A055]/50 ${filter === a.artwork_id ? "border-[#C9A055]/50 bg-[#C9A055]/5" : "border-white/10"}`}
                  data-testid={`mc-registry-artwork-${a.artwork_id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs text-[#F5F2EB] font-medium truncate pr-2">{a.title}</p>
                    <span className="text-[8px] tracking-[0.2em] uppercase px-2 py-0.5 flex-shrink-0"
                      style={{ background: a.art_type === "Original" ? "rgba(255,59,48,0.15)" : "rgba(201,160,85,0.15)", color: a.art_type === "Original" ? "#FF3B30" : "#C9A055", border: `1px solid ${a.art_type === "Original" ? "rgba(255,59,48,0.3)" : "rgba(201,160,85,0.3)"}` }}>
                      {a.art_type}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-white/5 overflow-hidden">
                      <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: pct === 100 ? "#4ade80" : "#C9A055" }} />
                    </div>
                    <span className="text-[10px] font-bold text-[#F5F2EB] flex-shrink-0" style={{ fontFamily: "'Saira Condensed', sans-serif" }}>
                      {a.claimed}/{a.total}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


/* ─── Price Tag Modal (4×6 Landscape) ─── */
function PriceTagModal({ art, onClose }) {
  const handlePrint = () => {
    const html = generateTagHtml(art);
    const win = window.open("", "_blank", "width=700,height=500");
    win.document.write(`<!DOCTYPE html><html><head>
      <title>Price Tag — ${art.title}</title>
      <link rel="preconnect" href="https://fonts.googleapis.com">
      <link href="https://fonts.googleapis.com/css2?family=Saira+Condensed:wght@700;800;900&family=DM+Sans:wght@400;500;700&family=Permanent+Marker&display=swap" rel="stylesheet">
      <style>
        @page { size: 6in 4in landscape; margin: 0; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { width: 6in; height: 4in; overflow: hidden; background: #fff; }
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style>
    </head><body>${html}</body></html>`);
    win.document.close();
    win.onload = () => { win.focus(); win.print(); };
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="mc-price-tag-modal">
      <div className="bg-[#141210] border border-white/10 max-w-3xl w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Package size={16} className="text-[#C9A055]" />
            <span className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#C9A055]">PRICE TAG — {art.title}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handlePrint} className="btn-gold text-[10px] flex items-center gap-1.5" data-testid="mc-price-tag-print">
              <Printer size={12} /> PRINT TAG
            </button>
            <button onClick={onClose} className="p-1 text-[#8a857d] hover:text-white"><X size={16} /></button>
          </div>
        </div>
        <div className="p-6 flex justify-center overflow-auto">
          <div dangerouslySetInnerHTML={{ __html: generateTagHtml(art) }} style={{ border: "1px solid #333" }} />
        </div>
      </div>
    </div>
  );
}
